import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/useApp';
import { lawyerService, userService } from '../services/firebaseService';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import toast from 'react-hot-toast';
import { 
  ArrowLeft, 
  Upload, 
  CreditCard, 
  Camera, 
  Linkedin, 
  Facebook, 
  Instagram, 
  User,
  MapPin,
  GraduationCap,
  Clock,
  DollarSign,
  FileText,
  Mail,
  Shield,
  Plus,
  X,
  Zap,
  ZapOff,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Calendar,
  BookOpen,
  Eye,
  EyeOff,
  IndianRupeeIcon,
  Phone,
  MessageSquare,
  Video,
  Trophy,
  PiggyBank,
  Share2,
  Twitter
} from 'lucide-react';



const LawyerRegistration = () => {
  const navigate = useNavigate();
  const { lawyers, setLawyers, clearStorageSpace } = useApp();
  const { user: currentUser } = useFirebaseAuth();
  

  
  const [language, setLanguage] = useState('english');
  const [showToast, setShowToast] = useState('');
  const [customLocation, setCustomLocation] = useState('');
  const [showCustomLocation, setShowCustomLocation] = useState(false);
  const [customSpecialization, setCustomSpecialization] = useState('');
  const [showCustomSpecialization, setShowCustomSpecialization] = useState(false);
  const [customDegree, setCustomDegree] = useState('');
  const [showCustomDegree, setShowCustomDegree] = useState(false);
  const [customQualification, setCustomQualification] = useState('');
  const [showCustomQualification, setShowCustomQualification] = useState(false);
  const [selectedSpecializations, setSelectedSpecializations] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraTargetField, setCameraTargetField] = useState('profilePicture');
  const [flashEnabled, setFlashEnabled] = useState(false);
  const [faceVerificationMatch, setFaceVerificationMatch] = useState(null);
  const [selectedDays, setSelectedDays] = useState([]);
  const [timeSlots, setTimeSlots] = useState({});
  const [profilePreview, setProfilePreview] = useState(null);
  const [faceVerificationPreview, setFaceVerificationPreview] = useState(null);
  const [emailValidationStatus, setEmailValidationStatus] = useState(null); // 'checking', 'unique', 'duplicate', 'error'
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  
  const [formData, setFormData] = useState({
    profilePicture: null,
    fullName: '',
    mobileNumber: '',
    location: '',
    specialization: [],
    yearsOfPractice: '',
    degree: '',
    consultationFee: '',
    professionalBio: '',
    languages: [],
    governmentId: null,
    faceVerification: null,
    barCouncilLicense: '',
    linkedin: '',
    gmail: '',
    facebook: '',
    instagram: '',
    twitter: '',
    availableForConsultation: true,
    timeSlots: {},
    availableDays: [],
    yearOfPassing: '',
    chamberAddress: '',
    barCouncilCertificate: null,
    selfieWithId: null,
    idProofType: '',
    idProof: null,
    textConsultationFee: '',
    callConsultationFee: '',
    videoCallConsultationFee: '',
    inPersonConsultationFee: '',
    emergencyConsultationAvailable: false,
    emergencyConsultationFee: '',
    fileReadingCharges: [],
    newFileChargeFromPage: '',
    newFileChargeToPage: '',
    newFileChargeAmount: '',
    accountNumber: '',
    accountHolderName: '',
    bankName: '',
    ifscCode: '',
    achievements: [],
    newAchievementName: '',
    newAchievementYear: '',
    termsAgreement: false
  });

  // Initialize selectedDays from formData.availableDays
  useEffect(() => {
    if (formData.availableDays && formData.availableDays.length > 0) {
      console.log('Initializing selectedDays from formData:', formData.availableDays);
      setSelectedDays(formData.availableDays);
    }
  }, [formData.availableDays]);

  // Load pending lawyer profile if it exists (only if not coming from successful creation)
  useEffect(() => {
    const pendingLawyerProfile = localStorage.getItem('pendingLawyerProfile');
    const profileJustCreated = sessionStorage.getItem('profileJustCreated');
    const lawyerProfileCreated = localStorage.getItem('lawyerProfileCreated');
    const isAuthenticated = localStorage.getItem('signInSuccessful');
    
    // Only load pending profile if we haven't just created one and we're not coming from auth
    if (pendingLawyerProfile && !profileJustCreated && !lawyerProfileCreated && !isAuthenticated) {
      try {
        const profileData = JSON.parse(pendingLawyerProfile);
        console.log('Loading pending lawyer profile:', profileData);
        
        // Populate form data with pending profile
        setFormData(prev => ({
          ...prev,
          fullName: profileData.full_name || '',
          mobileNumber: profileData.phone || '',
          location: profileData.bar_council_state || '',
          gmail: profileData.email || '',
          barCouncilLicense: profileData.bar_council_number || '',
          professionalBio: profileData.bio || '',
          consultationFee: profileData.consultation_fee?.toString() || '',
          textConsultationFee: profileData.text_consultation_fee?.toString() || '',
          callConsultationFee: profileData.call_consultation_fee?.toString() || '',
          videoCallConsultationFee: profileData.video_call_consultation_fee?.toString() || '',
          inPersonConsultationFee: profileData.in_person_consultation_fee?.toString() || '',
          emergencyConsultationAvailable: profileData.emergency_consultation_available || false,
          emergencyConsultationFee: profileData.emergency_consultation_fee?.toString() || '',
          fileReadingCharges: profileData.file_reading_charges || [],
          achievements: profileData.achievements || [],
          linkedin: profileData.linkedin || '',
          facebook: profileData.facebook || '',
          instagram: profileData.instagram || '',
          twitter: profileData.twitter || '',
          chamberAddress: profileData.chamber_address || '',
          yearOfPassing: profileData.year_of_passing || ''
        }));
        
        // Set specializations and languages
        if (profileData.specialization) {
          setSelectedSpecializations(Array.isArray(profileData.specialization) ? profileData.specialization : [profileData.specialization]);
        }
        
        if (profileData.languages) {
          setSelectedLanguages(Array.isArray(profileData.languages) ? profileData.languages : [profileData.languages]);
        }
        
        // Set availability
        if (profileData.availability?.available_days) {
          setSelectedDays(profileData.availability.available_days);
        }
        
        if (profileData.availability?.time_slots) {
          setTimeSlots(profileData.availability.time_slots);
        }
        
        // Set experiences and education
        if (profileData.experiences) {
          setExperiences(profileData.experiences);
        }
        
        if (profileData.education) {
          setEducation(profileData.education);
        }
        
        toast.success('Loaded your previously created profile data!');
      } catch (error) {
        console.error('Error loading pending lawyer profile:', error);
        toast.error('Failed to load previous profile data');
      }
    }
  }, []);

  // Cleanup countdown timer on unmount
  useEffect(() => {
    return () => {
      // Clear any existing countdown timer when component unmounts
      const existingInterval = window.countdownInterval;
      if (existingInterval) {
        clearInterval(existingInterval);
        console.log('Cleaned up countdown timer on unmount');
      }
    };
  }, []);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [countdown, setCountdown] = useState(10);

  // Fallback navigation effect - if success message is shown for too long, force navigation
  useEffect(() => {
    if (showSuccessMessage) {
      const fallbackTimer = setTimeout(() => {
        console.log('Fallback navigation triggered - navigating to /success');
        localStorage.setItem('lawyerProfileCreated', 'true');
        window.location.href = '/success';
      }, 15000); // 15 seconds fallback

      return () => clearTimeout(fallbackTimer);
    }
  }, [showSuccessMessage]);
  const [fieldErrors, setFieldErrors] = useState({
    fullName: '',
    mobileNumber: '',
    yearsOfPractice: '',
    consultationFee: '',
    barCouncilLicense: '',
    gmail: '',
    linkedin: '',
    facebook: '',
    instagram: ''
  });

  // Progress tracking
  const [progress, setProgress] = useState(0);

  // Practice Areas Data
  const practiceAreasData = [
    {
      "practice": "Criminal Law",
      "areas": [
        {
          "name": "White Collar Crime",
          "sub_areas": ["Fraud", "Corruption Cases", "Money Laundering"]
        },
        {
          "name": "Cyber Crime",
          "sub_areas": ["Phishing", "Hacking", "Social Media Defamation"]
        },
        {
          "name": "Narcotics & Drugs",
          "sub_areas": ["NDPS Act Cases", "Drug Possession Defense"]
        },
        {
          "name": "Sexual Offenses",
          "sub_areas": ["POCSO Act Cases", "Rape Defense", "Harassment Cases"]
        },
        {
          "name": "Violent Crimes",
          "sub_areas": ["Murder Defense", "Assault", "Attempt to Murder"]
        },
        {
          "name": "Traffic Offenses",
          "sub_areas": ["Drunk Driving", "Hit & Run Defense"]
        }
      ]
    },
    {
      "practice": "Civil Law",
      "areas": [
        {
          "name": "Property Disputes",
          "sub_areas": ["Land Title Disputes", "Encroachment", "Partition Suits"]
        },
        {
          "name": "Contract Law",
          "sub_areas": ["Breach of Contract", "Specific Performance", "Contract Drafting"]
        },
        {
          "name": "Tort Law",
          "sub_areas": ["Defamation", "Negligence", "Consumer Damages"]
        },
        {
          "name": "Recovery Matters",
          "sub_areas": ["Debt Recovery", "Loan Disputes", "Cheque Bounce Cases"]
        }
      ]
    },
    {
      "practice": "Corporate / Commercial Law",
      "areas": [
        {
          "name": "Mergers & Acquisitions",
          "sub_areas": ["Due Diligence", "Transaction Structuring"]
        },
        {
          "name": "Company Law",
          "sub_areas": ["Incorporation", "ROC Filings", "Shareholder Disputes"]
        },
        {
          "name": "Contracts",
          "sub_areas": ["Drafting", "Review", "Negotiation"]
        },
        {
          "name": "Compliance",
          "sub_areas": ["SEBI Compliance", "RBI Compliance", "GST Corporate Compliance"]
        },
        {
          "name": "Banking & Finance",
          "sub_areas": ["Loan Agreements", "Debt Restructuring"]
        }
      ]
    },
    {
      "practice": "Family Law",
      "areas": [
        {
          "name": "Divorce",
          "sub_areas": ["Mutual Consent", "Contested Divorce", "NRI Divorce"]
        },
        {
          "name": "Child Custody",
          "sub_areas": ["Guardianship", "Visitation Rights"]
        },
        {
          "name": "Domestic Violence",
          "sub_areas": ["Protection Orders", "Criminal Complaints"]
        },
        {
          "name": "Adoption",
          "sub_areas": ["Domestic Adoption", "Inter-country Adoption"]
        },
        {
          "name": "Maintenance & Alimony",
          "sub_areas": ["Claim Filing", "Enforcement"]
        }
      ]
    },
    {
      "practice": "Constitutional Law",
      "areas": [
        {
          "name": "Writ Petitions",
          "sub_areas": ["Habeas Corpus", "Mandamus", "Certiorari"]
        },
        {
          "name": "Fundamental Rights",
          "sub_areas": ["Violation Cases", "PILs"]
        },
        {
          "name": "Election Law",
          "sub_areas": ["Disqualification Matters", "Election Petitions"]
        }
      ]
    },
    {
      "practice": "Property & Real Estate Law",
      "areas": [
        {
          "name": "Land Title Verification",
          "sub_areas": []
        },
        {
          "name": "Lease & Rent Disputes",
          "sub_areas": []
        },
        {
          "name": "RERA Matters",
          "sub_areas": []
        },
        {
          "name": "Builder-Buyer Disputes",
          "sub_areas": []
        },
        {
          "name": "Sale/Purchase Agreements",
          "sub_areas": []
        }
      ]
    },
    {
      "practice": "Intellectual Property Law",
      "areas": [
        {
          "name": "Trademark",
          "sub_areas": ["Registration", "Opposition", "Infringement"]
        },
        {
          "name": "Copyright",
          "sub_areas": ["Licensing", "Piracy", "Ownership Disputes"]
        },
        {
          "name": "Patents",
          "sub_areas": ["Filing", "Opposition", "Infringement Defense"]
        },
        {
          "name": "Trade Secrets",
          "sub_areas": ["Protection", "Breach Actions"]
        }
      ]
    },
    {
      "practice": "Labour & Employment Law",
      "areas": [
        {
          "name": "Employment Contracts",
          "sub_areas": []
        },
        {
          "name": "Wrongful Termination",
          "sub_areas": []
        },
        {
          "name": "Workplace Harassment (POSH Act)",
          "sub_areas": []
        },
        {
          "name": "Industrial Disputes",
          "sub_areas": []
        },
        {
          "name": "Wage & Bonus Claims",
          "sub_areas": []
        }
      ]
    },
    {
      "practice": "Taxation Law",
      "areas": [
        {
          "name": "Income Tax",
          "sub_areas": ["Assessments", "Appeals", "Refunds"]
        },
        {
          "name": "GST",
          "sub_areas": ["Registration", "Compliance", "Disputes"]
        },
        {
          "name": "Customs & Excise",
          "sub_areas": ["Import/Export Disputes", "Duty Evasion"]
        }
      ]
    },
    {
      "practice": "Consumer Protection Law",
      "areas": [
        {
          "name": "Consumer Court Complaints",
          "sub_areas": []
        },
        {
          "name": "Defective Goods Cases",
          "sub_areas": []
        },
        {
          "name": "Service Deficiency Claims",
          "sub_areas": []
        }
      ]
    },
    {
      "practice": "Banking & Finance Law",
      "areas": [
        {
          "name": "Loan Disputes",
          "sub_areas": []
        },
        {
          "name": "Cheque Bounce (Section 138)",
          "sub_areas": []
        },
        {
          "name": "Debt Recovery Tribunal (DRT) Cases",
          "sub_areas": []
        }
      ]
    },
    {
      "practice": "Environmental Law",
      "areas": [
        {
          "name": "Pollution Control Cases",
          "sub_areas": []
        },
        {
          "name": "Wildlife Protection",
          "sub_areas": []
        },
        {
          "name": "EIA Approvals & Disputes",
          "sub_areas": []
        }
      ]
    },
    {
      "practice": "Immigration Law",
      "areas": [
        {
          "name": "Visa Issues",
          "sub_areas": []
        },
        {
          "name": "Deportation Defense",
          "sub_areas": []
        },
        {
          "name": "Citizenship Applications",
          "sub_areas": []
        }
      ]
    },
    {
      "practice": "Cyber Law",
      "areas": [
        {
          "name": "Data Protection",
          "sub_areas": []
        },
        {
          "name": "Online Fraud Cases",
          "sub_areas": []
        },
        {
          "name": "Cyberbullying & Harassment",
          "sub_areas": []
        }
      ]
    },
    {
      "practice": "Arbitration & Dispute Resolution",
      "areas": [
        {
          "name": "Domestic Arbitration",
          "sub_areas": []
        },
        {
          "name": "International Arbitration",
          "sub_areas": []
        },
        {
          "name": "Mediation",
          "sub_areas": []
        }
      ]
    },
    {
      "practice": "Insurance Law",
      "areas": [
        {
          "name": "Health Insurance Claims",
          "sub_areas": []
        },
        {
          "name": "Motor Accident Claims (MACT)",
          "sub_areas": []
        },
        {
          "name": "Life Insurance Disputes",
          "sub_areas": []
        }
      ]
    },
    {
      "practice": "Medical & Health Law",
      "areas": [
        {
          "name": "Medical Negligence",
          "sub_areas": []
        },
        {
          "name": "Hospital Disputes",
          "sub_areas": []
        },
        {
          "name": "Licensing Issues",
          "sub_areas": []
        }
      ]
    },
    {
      "practice": "Maritime / Admiralty Law",
      "areas": [
        {
          "name": "Shipping Contracts",
          "sub_areas": []
        },
        {
          "name": "Cargo Disputes",
          "sub_areas": []
        },
        {
          "name": "Marine Insurance",
          "sub_areas": []
        }
      ]
    },
    {
      "practice": "Education Law",
      "areas": [
        {
          "name": "School/College Disputes",
          "sub_areas": []
        },
        {
          "name": "University Regulation Matters",
          "sub_areas": []
        },
        {
          "name": "Student Rights",
          "sub_areas": []
        }
      ]
    },
    {
      "practice": "Public Interest Litigation (PIL)",
      "areas": [
        {
          "name": "Social Welfare Matters",
          "sub_areas": []
        },
        {
          "name": "Human Rights Cases",
          "sub_areas": []
        },
        {
          "name": "Environmental PILs",
          "sub_areas": []
        }
      ]
    }
  ];

  // Experience management
  const [experiences, setExperiences] = useState([{
    subPracticeArea: '',
    casesHandled: '',
    yearsOfPractice: ''
  }]);

  // Education management
  const [education, setEducation] = useState([{
    qualification: '',
    degreeCollege: ''
  }]);
  const [selectedPracticeAreas, setSelectedPracticeAreas] = useState([]);
  const [selectedSubPracticeAreas, setSelectedSubPracticeAreas] = useState([]);
  const [showCustomPracticeArea, setShowCustomPracticeArea] = useState(false);
  const [customPracticeArea, setCustomPracticeArea] = useState('');
  const [showCustomSubPracticeArea, setShowCustomSubPracticeArea] = useState(false);
  const [customSubPracticeArea, setCustomSubPracticeArea] = useState('');

  // Calculate form completion progress
  const calculateProgress = useCallback(() => {
    const sections = [
      // Profile Picture
      { field: 'profilePicture', weight: 8 },
      // Basic Info
      { field: 'fullName', weight: 8 },
      { field: 'mobileNumber', weight: 8 },
      { field: 'location', weight: 8 },
      { field: 'professionalBio', weight: 8 },
      // Professional Details
      { field: 'specialization', weight: 8 },
      { field: 'education', weight: 8 },
      { field: 'consultationFee', weight: 8 },
      { field: 'languages', weight: 8 },
      { field: 'chamberAddress', weight: 6 },
      { field: 'yearOfPassing', weight: 6 },
      // Verification
      { field: 'idProof', weight: 6 },
      { field: 'barCouncilCertificate', weight: 6 },
      { field: 'selfieWithId', weight: 6 },
      { field: 'barCouncilLicense', weight: 6 },
      // Social & Contact
      { field: 'gmail', weight: 4 },
      { field: 'linkedin', weight: 4 },
      { field: 'facebook', weight: 4 },
      { field: 'instagram', weight: 4 },
      { field: 'twitter', weight: 4 },
      // Availability
      { field: 'availableDays', weight: 6 },
      { field: 'timeSlots', weight: 6 },
      // Terms
      { field: 'termsAgreement', weight: 4 }
    ];

    let completedWeight = 0;
    let totalWeight = 0;

    sections.forEach(section => {
      totalWeight += section.weight;
      const value = formData[section.field];
      
      if (section.field === 'specialization') {
        if (selectedSpecializations.length > 0) {
          completedWeight += section.weight;
        }
      } else if (section.field === 'languages') {
        if (selectedLanguages.length > 0) {
          completedWeight += section.weight;
        }
      } else if (section.field === 'education') {
        if (education.length > 0 && education.some(edu => edu.qualification && edu.degreeCollege)) {
          completedWeight += section.weight;
        }
      } else if (section.field === 'availableDays') {
        if (selectedDays.length > 0) {
          completedWeight += section.weight;
        }
      } else if (section.field === 'timeSlots') {
        if (Object.values(timeSlots).flat().length > 0) {
          completedWeight += section.weight;
        }
      } else if (section.field === 'profilePicture') {
        if (profilePreview) {
          completedWeight += section.weight;
        }
      } else if (section.field === 'idProof') {
        if (formData.idProof) {
          completedWeight += section.weight;
        }
      } else if (section.field === 'barCouncilCertificate') {
        if (formData.barCouncilCertificate) {
          completedWeight += section.weight;
        }
      } else if (section.field === 'selfieWithId') {
        if (formData.selfieWithId) {
          completedWeight += section.weight;
        }
      } else if (section.field === 'termsAgreement') {
        if (formData.termsAgreement) {
          completedWeight += section.weight;
        }
      } else if (section.field === 'barCouncilLicense') {
        if (formData.barCouncilLicense && BAR_LICENSE_REGEX.test(formData.barCouncilLicense)) {
          completedWeight += section.weight;
        }
      } else if (value && value !== '' && value !== null) {
        completedWeight += section.weight;
      }
    });

    // Add experience validation
    if (experiences.length > 0 && experiences.some(exp => exp.yearsOfPractice && exp.yearsOfPractice.trim() !== '')) {
      completedWeight += 8; // Add weight for experience
      totalWeight += 8;
    }


    
    // Temporarily make face verification optional for progress calculation
    // Face verification weight is still included but not required for 100%

    return Math.round((completedWeight / totalWeight) * 100);
  }, [formData, selectedSpecializations, selectedLanguages, selectedDays, timeSlots, profilePreview, experiences, education]);

  // Update progress whenever form data changes
  useEffect(() => {
    const newProgress = calculateProgress();
    setProgress(newProgress);
  }, [calculateProgress]);
  
  // Debug selectedDays changes
  useEffect(() => {
    console.log('selectedDays changed:', selectedDays);
  }, [selectedDays]);

  const setValidationError = (field, message) => {
    setFieldErrors(prev => ({ ...prev, [field]: message }));
  };

  // Generate time range between two times
  const generateTimeRange = (fromTime, toTime) => {
    console.log('Generating time range from:', fromTime, 'to:', toTime);
    const timeSlots = [];
    
    // Convert 12-hour format to 24-hour for easier comparison
    const convertTo24Hour = (time12h) => {
      const [time, modifier] = time12h.split(' ');
      let [hours, minutes] = time.split(':');
      hours = parseInt(hours);
      minutes = parseInt(minutes);
      
      if (hours === 12) {
        hours = modifier === 'PM' ? 12 : 0;
      } else if (modifier === 'PM') {
        hours = hours + 12;
      }
      
      return hours * 60 + minutes; // Convert to minutes for easier comparison
    };
    
    const convertTo12Hour = (minutes) => {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      return `${displayHours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')} ${ampm}`;
    };
    
    const fromMinutes = convertTo24Hour(fromTime);
    const toMinutes = convertTo24Hour(toTime);
    
    console.log('From minutes:', fromMinutes, 'To minutes:', toMinutes);
    
    // Generate 30-minute intervals between from and to times
    for (let minutes = fromMinutes; minutes <= toMinutes; minutes += 30) {
      const timeSlot = convertTo12Hour(minutes);
      timeSlots.push(timeSlot);
    }
    
    console.log('Generated time slots:', timeSlots);
    return timeSlots;
  };

  // Days of the week
  const daysOfWeek = [
    { value: 'monday', label: 'Monday', labelHindi: 'सोमवार' },
    { value: 'tuesday', label: 'Tuesday', labelHindi: 'मंगलवार' },
    { value: 'wednesday', label: 'Wednesday', labelHindi: 'बुधवार' },
    { value: 'thursday', label: 'Thursday', labelHindi: 'गुरुवार' },
    { value: 'friday', label: 'Friday', labelHindi: 'शुक्रवार' },
    { value: 'saturday', label: 'Saturday', labelHindi: 'शनिवार' },
    { value: 'sunday', label: 'Sunday', labelHindi: 'रविवार' }
  ];

  // Time slots
  const availableTimeSlots = [
    '09:00 AM - 10:00 AM', '10:00 AM - 11:00 AM', '11:00 AM - 12:00 PM',
    '12:00 PM - 01:00 PM', '01:00 PM - 02:00 PM', '02:00 PM - 03:00 PM',
    '03:00 PM - 04:00 PM', '04:00 PM - 05:00 PM', '05:00 PM - 06:00 PM',
    '06:00 PM - 07:00 PM', '07:00 PM - 08:00 PM', '08:00 PM - 09:00 PM'
  ];

  // Indian cities data
  const indianCities = [
    'Mumbai, Maharashtra', 'Delhi, Delhi', 'Bangalore, Karnataka', 'Hyderabad, Telangana',
    'Chennai, Tamil Nadu', 'Kolkata, West Bengal', 'Pune, Maharashtra', 'Ahmedabad, Gujarat',
    'Jaipur, Rajasthan', 'Surat, Gujarat', 'Lucknow, Uttar Pradesh', 'Kanpur, Uttar Pradesh',
    'Nagpur, Maharashtra', 'Indore, Madhya Pradesh', 'Thane, Maharashtra', 'Bhopal, Madhya Pradesh',
    'Visakhapatnam, Andhra Pradesh', 'Pimpri-Chinchwad, Maharashtra', 'Patna, Bihar', 'Vadodara, Gujarat'
  ];

  // Legal specializations
  const legalSpecializations = [
    'Civil Law', 'Criminal Law', 'Corporate Law', 'Family Law', 'Property Law',
    'Tax Law', 'Constitutional Law', 'Labour Law', 'Intellectual Property Law',
    'Environmental Law', 'Banking Law', 'Insurance Law', 'Real Estate Law',
    'Immigration Law', 'International Law', 'Media Law', 'Sports Law',
    'Healthcare Law', 'Technology Law', 'Arbitration Law'
  ];

  // Law colleges and universities
  const lawInstitutions = [
    'National Law School of India University, Bangalore',
    'National Academy of Legal Studies and Research, Hyderabad',
    'National Law Institute University, Bhopal',
    'National University of Juridical Sciences, Kolkata',
    'Gujarat National Law University, Gandhinagar',
    'Rajiv Gandhi National University of Law, Patiala',
    'Hidayatullah National Law University, Raipur',
    'Chanakya National Law University, Patna',
    'Dr. Ram Manohar Lohiya National Law University, Lucknow',
    'Maharashtra National Law University, Mumbai',
    'Tamil Nadu National Law University, Tiruchirappalli',
    'Damodaram Sanjivayya National Law University, Visakhapatnam',
    'Himachal Pradesh National Law University, Shimla',
    'National Law University, Jodhpur',
    'National Law University, Delhi',
    'National Law University, Odisha',
    'National Law University and Judicial Academy, Assam',
    'National Law University, Tripura',
    'National Law University, Meghalaya',
    'National Law University, Nagaland',
    'Harvard Law School, USA',
    'Yale Law School, USA',
    'Stanford Law School, USA',
    'Columbia Law School, USA',
    'University of Oxford Faculty of Law, UK',
    'University of Cambridge Faculty of Law, UK',
    'London School of Economics, UK',
    'University of Toronto Faculty of Law, Canada',
    'University of Melbourne Law School, Australia',
    'National University of Singapore Faculty of Law, Singapore'
  ];

  // Law qualifications
  const lawQualifications = [
    'LL.B. (Bachelor of Laws)',
    'B.A.LL.B. (Bachelor of Arts and Bachelor of Laws)',
    'B.Com.LL.B. (Bachelor of Commerce and Bachelor of Laws)',
    'B.Sc.LL.B. (Bachelor of Science and Bachelor of Laws)',
    'BBA.LL.B. (Bachelor of Business Administration and Bachelor of Laws)',
    'LL.M. (Master of Laws)',
    'Ph.D. in Law',
    'Diploma in Law',
    'Certificate in Law'
  ];

  // Top 10 Indian Languages
  const indianLanguages = [
    { value: 'english', label: 'English', labelHindi: 'अंग्रेज़ी' },
    { value: 'hindi', label: 'Hindi', labelHindi: 'हिंदी' },
    { value: 'bengali', label: 'Bengali', labelHindi: 'बंगाली' },
    { value: 'telugu', label: 'Telugu', labelHindi: 'तेलुगु' },
    { value: 'marathi', label: 'Marathi', labelHindi: 'मराठी' },
    { value: 'tamil', label: 'Tamil', labelHindi: 'तमिल' },
    { value: 'gujarati', label: 'Gujarati', labelHindi: 'गुजराती' },
    { value: 'kannada', label: 'Kannada', labelHindi: 'कन्नड़' },
    { value: 'malayalam', label: 'Malayalam', labelHindi: 'मलयालम' },
    { value: 'punjabi', label: 'Punjabi', labelHindi: 'पंजाबी' }
  ];

  const translations = {
    english: {
      title: 'Create Lawyer Profile',
      subtitle: 'Complete your professional details',
      backButton: 'Back',
      profilePicture: 'Upload Your Profile Picture',
      uploadPhoto: 'Tap to upload photo',
      takePhoto: 'Take Photo',
      uploadFromPhone: 'Upload from Phone',
      fullName: 'Full Name',
      fullNamePlaceholder: 'Full Name (as per legal documents)',
      location: 'Location',
      locationPlaceholder: 'Select your location',
      addCustomLocation: 'Add Custom Location',
      customLocationPlaceholder: 'Enter your city name',
      specialization: 'Specialization',
      specializationPlaceholder: 'Select your areas of expertise',
      addCustomSpecialization: 'Add Custom Expertise',
      customSpecializationPlaceholder: 'Enter your area of expertise',
      yearsOfPractice: 'Years of Practice',
      yearsPlaceholder: 'Enter years of practice',
      study: 'Study',
      qualification: 'Qualification',
      qualificationPlaceholder: 'Select your qualification',
      degreeCollege: 'Degree College',
      degreeCollegePlaceholder: 'Select your college',
      addCustomCollege: 'Add Custom College',
      customCollegePlaceholder: 'Enter your college name',
      addCustomQualification: 'Add Custom Qualification',
      customQualificationPlaceholder: 'Enter your qualification',
      addMoreStudy: 'Add More Study',
      consultationFee: 'Consultation Fee (per session)',
      feePlaceholder: 'Enter consultation fee',
      professionalBio: 'Professional Bio',
      bioPlaceholder: 'Tell us about your experience and practice areas.',
      bioHelper: 'Share your expertise and what clients can expect.',
      identityVerification: 'Identity Verification',
      identitySubtitle: 'For security, please verify your identity. This information is not public.',
      uploadIdCard: 'Upload ID Card/Aadhaar/PAN/Passport',
      barCouncilLicense: 'Bar Council License Number',
      barCouncilPlaceholder: 'Enter your Bar Council license number',
      liveFaceVerification: 'Live Face Verification',
      socialPresence: 'Social Presence',
      linkedinProfile: 'LinkedIn Profile URL',
      facebookProfile: 'Facebook Profile URL',
      instagramProfile: 'Instagram Profile URL',
      gmail: 'Gmail ID',
      gmailPlaceholder: 'Enter your gmail address',
      availabilitySchedule: 'Availability Schedule',
      availableDays: 'Available Days',
      availableTimeSlots: 'Available Time Slots',
      createProfile: 'Create Profile',
      required: 'Required',
      optional: 'Optional',
      flashOn: 'Flash On',
      flashOff: 'Flash Off',
      capturePhoto: 'Capture Photo',
      retakePhoto: 'Retake Photo',
      usePhoto: 'Use Photo',
      faceMatchSuccess: 'Face verification successful!',
      faceMatchFailed: 'Face verification failed. Please try again.',
      selectDays: 'Select available days',
      selectTimeSlots: 'Select available time slots'
    },
    hindi: {
      title: 'वकील प्रोफाइल बनाएं',
      subtitle: 'अपने पेशेवर विवरण पूरे करें',
      backButton: 'वापस',
      profilePicture: 'अपनी प्रोफाइल फोटो अपलोड करें',
      uploadPhoto: 'फोटो अपलोड करने के लिए टैप करें',
      takePhoto: 'फोटो लें',
      uploadFromPhone: 'फोन से अपलोड करें',
      fullName: 'पूरा नाम',
      fullNamePlaceholder: 'पूरा नाम (कानूनी दस्तावेजों के अनुसार)',
      location: 'स्थान',
      locationPlaceholder: 'अपना स्थान चुनें',
      addCustomLocation: 'कस्टम स्थान जोड़ें',
      customLocationPlaceholder: 'अपने शहर का नाम दर्ज करें',
      specialization: 'विशेषज्ञता',
      specializationPlaceholder: 'अपनी विशेषज्ञता के क्षेत्र चुनें',
      addCustomSpecialization: 'कस्टम विशेषज्ञता जोड़ें',
      customSpecializationPlaceholder: 'अपनी विशेषज्ञता का क्षेत्र दर्ज करें',
      yearsOfPractice: 'अभ्यास के वर्ष',
      yearsPlaceholder: 'अभ्यास के वर्ष दर्ज करें',
      study: 'शिक्षा',
      qualification: 'योग्यता',
      qualificationPlaceholder: 'अपनी योग्यता चुनें',
      degreeCollege: 'डिग्री कॉलेज',
      degreeCollegePlaceholder: 'अपना कॉलेज चुनें',
      addCustomCollege: 'कस्टम कॉलेज जोड़ें',
      customCollegePlaceholder: 'अपने कॉलेज का नाम दर्ज करें',
      addCustomQualification: 'कस्टम योग्यता जोड़ें',
      customQualificationPlaceholder: 'अपनी योग्यता दर्ज करें',
      addMoreStudy: 'और शिक्षा जोड़ें',
      consultationFee: 'परामर्श शुल्क (प्रति सत्र)',
      feePlaceholder: 'परामर्श शुल्क दर्ज करें',
      professionalBio: 'पेशेवर जीवनी',
      bioPlaceholder: 'अपने अनुभव और अभ्यास क्षेत्रों के बारे में बताएं।',
      bioHelper: 'अपनी विशेषज्ञता और ग्राहकों की उम्मीदों के बारे में बताएं।',
      identityVerification: 'पहचान सत्यापन',
      identitySubtitle: 'सुरक्षा के लिए, कृपया अपनी पहचान सत्यापित करें। यह जानकारी सार्वजनिक नहीं है।',
      uploadIdCard: 'आईडी कार्ड/आधार/पैन/पासपोर्ट अपलोड करें',
      barCouncilLicense: 'बार काउंसिल लाइसेंस नंबर',
      barCouncilPlaceholder: 'अपना बार काउंसिल लाइसेंस नंबर दर्ज करें',
      liveFaceVerification: 'लाइव फेस वेरिफिकेशन',
      socialPresence: 'सामाजिक उपस्थिति',
      linkedinProfile: 'लिंक्डइन प्रोफाइल URL',
      facebookProfile: 'फेसबुक प्रोफाइल URL',
      instagramProfile: 'इंस्टाग्राम प्रोफाइल URL',
      gmail: 'जीमेल ID',
      gmailPlaceholder: 'अपना जीमेल पता दर्ज करें',
      availabilitySchedule: 'उपलब्धता कार्यक्रम',
      availableDays: 'उपलब्ध दिन',
      availableTimeSlots: 'उपलब्ध समय स्लॉट',
      createProfile: 'प्रोफाइल बनाएं',
      required: 'आवश्यक',
      optional: 'वैकल्पिक',
      flashOn: 'फ्लैश ऑन',
      flashOff: 'फ्लैश ऑफ',
      capturePhoto: 'फोटो कैप्चर करें',
      retakePhoto: 'फिर से फोटो लें',
      usePhoto: 'फोटो का उपयोग करें',
      faceMatchSuccess: 'चेहरा सत्यापन सफल!',
      faceMatchFailed: 'चेहरा सत्यापन असफल। कृपया फिर से कोशिश करें।',
      selectDays: 'उपलब्ध दिन चुनें',
      selectTimeSlots: 'उपलब्ध समय स्लॉट चुनें'
    }
  };

  const t = translations[language] || translations.english;
  const GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY;
  const BAR_LICENSE_REGEX = /^[A-Z]{1,5}\/\d{1,6}\/\d{4}$/;
  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const URL_REGEX = /^https?:\/\/.+/;

  // Draft persistence keys
  const DRAFT_KEY = 'lawyerRegistrationDraft_v1';

  // Load draft on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const draft = JSON.parse(raw);
        if (draft && typeof draft === 'object') {
          setFormData(prev => ({ ...prev, ...draft.formData }));
          setSelectedDays(draft.selectedDays || []);
          setTimeSlots(draft.timeSlots || {});
          setProfilePreview(draft.profilePreview || null);
          setFaceVerificationPreview(draft.faceVerificationPreview || null);
          setFaceVerificationMatch(draft.faceVerificationMatch || null);
          setLanguage(draft.language || 'english');
          setEducation(draft.education || [{ qualification: '', degreeCollege: '' }]);
        }
      }
    } catch (_) { /* ignore corrupt draft */ }
  }, []);

  // Clear validation errors on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (Object.values(fieldErrors).some(Boolean)) {
        setFieldErrors({});
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [fieldErrors]);

  // Save draft whenever important fields change
  useEffect(() => {
    const draft = {
      formData: {
        ...formData,
        // Files cannot be serialized; drop them from draft
        profilePicture: null,
        governmentId: null,
        faceVerification: null,
      },
      selectedDays,
      timeSlots,
      profilePreview,
      faceVerificationPreview,
      faceVerificationMatch,
      language,
      education,
    };
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(draft)); } catch (_) { /* ignore quota */ }
  }, [formData, selectedDays, timeSlots, profilePreview, faceVerificationPreview, faceVerificationMatch, language, education]);

  // AI Face verification (Gemini fallback + on-device dHash)
  const verifyFaceMatch = useCallback(async (profileFile, verificationFile) => {
    const fileToBase64 = (file) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        resolve(typeof result === 'string' ? result.split(',')[1] : '');
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const verifyWithGemini = async (fileA, fileB) => {
      if (!GEMINI_API_KEY) {
        console.log('Gemini API key not found, falling back to local verification');
        return null;
      }
      try {
        // Preprocess images to improve face detection
        const preprocessImage = async (file) => {
          return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              
              // Set canvas size for better face detection
              canvas.width = 512;
              canvas.height = 512;
              
              // Calculate scaling to fit image in canvas while maintaining aspect ratio
              const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
              const scaledWidth = img.width * scale;
              const scaledHeight = img.height * scale;
              
              // Center the image in the canvas
              const x = (canvas.width - scaledWidth) / 2;
              const y = (canvas.height - scaledHeight) / 2;
              
              // Draw the image centered
              ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
              
              // Convert to base64
              const base64 = canvas.toDataURL('image/jpeg', 0.9).split(',')[1];
              resolve(base64);
            };
            img.src = URL.createObjectURL(file);
          });
        };
        
        const [a64, b64] = await Promise.all([
          preprocessImage(fileA), 
          preprocessImage(fileB)
        ]);
        const prompt = `Analyze these two images and determine if they show the same person.

// IMPORTANT INSTRUCTIONS:
- Focus on facial features, structure, and characteristics regardless of position in frame
- Consider different angles, lighting, and expressions
- Look for key identifying features like eyes, nose, mouth shape, face structure
- Position differences (left/right/center) should not affect the result
- Be reasonable but accurate - if the core facial features match, return true

Return ONLY a JSON object in this exact format:
{"isHuman": true/false, "isSamePerson": true/false}

If both images show human faces and appear to be the same person (even with different positioning), return:
{"isHuman": true, "isSamePerson": true}

If faces don't match or are not human, return:
{"isHuman": true, "isSamePerson": false}`;
        
        const res = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent?key=${GEMINI_API_KEY}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contents: [{
              parts: [
                { text: prompt },
                { inline_data: { mime_type: 'image/jpeg', data: a64 } },
                { inline_data: { mime_type: 'image/jpeg', data: b64 } }
              ]
            }],
            generationConfig: {
              temperature: 0.1,
              topK: 1,
              topP: 0.1,
              maxOutputTokens: 100
            }
          })
        });
        
        if (!res.ok) {
          console.error('Gemini API error:', res.status, res.statusText);
          return null;
        }
        
        const json = await res.json();
        console.log('Gemini API response:', json);
        
        const text = json?.candidates?.[0]?.content?.parts?.[0]?.text || '';
        console.log('Gemini response text:', text);
        
        // Try to extract JSON from the response
        const match = text.match(/\{[\s\S]*\}/);
        if (match) {
          const obj = JSON.parse(match[0]);
          if (typeof obj.isHuman === 'boolean' && typeof obj.isSamePerson === 'boolean') {
            console.log('Gemini verification result:', obj);
            return obj;
          }
        }
        
        console.log('Failed to parse Gemini response as valid JSON');
        return null;
      } catch (error) {
        console.error('Gemini API error:', error);
        return null;
      }
    };
    const loadImageFromFile = (file) =>
      new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => resolve({ img, url });
        img.onerror = reject;
        img.src = url;
      });

    const detectFaceBox = async (img) => {
      try {
        // Use FaceDetector API when available (Chrome / Edge)
        if (typeof window !== 'undefined' && 'FaceDetector' in window) {
          const detector = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 1 });
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          const faces = await detector.detect(canvas);
          if (faces && faces.length > 0) {
            const { x, y, width, height } = faces[0].boundingBox;
            return { x: Math.max(0, x), y: Math.max(0, y), width, height };
          }
        }
      } catch (_) { /* ignore and fallback */ }
      // Fallback: center-crop square region
      const size = Math.min(img.width, img.height) * 0.6;
      return { x: (img.width - size) / 2, y: (img.height - size) / 2, width: size, height: size };
    };

    const computeDHash = (img, box) => {
      // Crop and scale to 9x8, compute horizontal gradient hash (64 bits)
      const w = 9, h = 8;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, box.x, box.y, box.width, box.height, 0, 0, w, h);
      const { data } = ctx.getImageData(0, 0, w, h);
      const gray = [];
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i], g = data[i + 1], b = data[i + 2];
        gray.push((r * 0.299 + g * 0.587 + b * 0.114));
      }
      const bits = [];
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w - 1; x++) {
          const idx = y * w + x;
          bits.push(gray[idx] < gray[idx + 1] ? 1 : 0);
        }
      }
      return bits; // length 64
    };

    const hammingDistance = (a, b) => a.reduce((acc, bit, i) => acc + (bit !== b[i] ? 1 : 0), 0);

    const { img: imgA, url: urlA } = await loadImageFromFile(profileFile);
    const { img: imgB, url: urlB } = await loadImageFromFile(verificationFile);
    try {
      // Prefer cloud verification if key is set
      const gem = await verifyWithGemini(profileFile, verificationFile);
      if (gem) return gem;
      const boxA = await detectFaceBox(imgA);
      const boxB = await detectFaceBox(imgB);
      // Basic human presence check: if FaceDetector present and found no faces, fail
      if (('FaceDetector' in window) && (!boxA || !boxB)) {
        return { isHuman: false, isSamePerson: false };
      }
      const hashA = computeDHash(imgA, boxA);
      const hashB = computeDHash(imgB, boxB);
      const dist = hammingDistance(hashA, hashB);
      const isSame = dist <= 10; // heuristic threshold
      return { isHuman: true, isSamePerson: isSame };
    } finally {
      URL.revokeObjectURL(urlA);
      URL.revokeObjectURL(urlB);
    }
  }, []);

  // Camera functions
  const startCamera = async (target = 'profilePicture') => {
    setCameraTargetField(target);
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        showNotification('Camera not supported in this browser', 'error');
        return;
      }

      if (target === 'faceVerification' && !formData.profilePicture && !profilePreview) {
        showNotification('Upload or take a profile photo before live verification.', 'error');
        return;
      }

      // Block early if not secure context (required on mobile when accessed via LAN IP)
      const isLocalhost = ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);
      if (!window.isSecureContext && !isLocalhost) {
        showNotification('Camera requires HTTPS. Open the app on https or localhost.', 'error');
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 }
        },
        audio: false
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        const onReady = async () => {
          try {
            await videoRef.current.play();
          } catch (_) {
            /* ignore autoplay errors */
          }
        };
        if ('onloadedmetadata' in videoRef.current) {
          videoRef.current.onloadedmetadata = onReady;
        } else {
          setTimeout(onReady, 300);
        }
      }
      setShowCamera(true);
    } catch (error) {
      let message = 'Unable to start camera';
      if (error && typeof error === 'object') {
        switch (error.name) {
          case 'NotAllowedError':
          case 'SecurityError':
            message = 'Camera permission denied. Please allow access in the browser.';
            break;
          case 'NotFoundError':
          case 'OverconstrainedError':
            message = 'No compatible camera found on this device.';
            break;
          case 'NotReadableError':
            message = 'Camera is in use by another application.';
            break;
          case 'DOMException':
            if (String(error.message || '').toLowerCase().includes('secure')) {
              message = 'Camera requires HTTPS. Open the app on https or localhost.';
            }
            break;
          default:
            break;
        }
      }
      showNotification(message, 'error');
      setShowCamera(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    setShowCamera(false);
  };

  // Ensure the stream attaches after the modal (video element) is rendered
  useEffect(() => {
    if (!showCamera) return;
    const stream = streamRef.current;
    const video = videoRef.current;
    if (!stream || !video) return;
    try {
      video.srcObject = stream;
      video.muted = true;
      const onReady = async () => {
        try { await video.play(); } catch (_) { /* ignore */ }
      };
      if ('onloadedmetadata' in video) {
        video.onloadedmetadata = onReady;
      } else {
        setTimeout(onReady, 300);
      }
    } catch (_) {
      // no-op; any error will be surfaced via startCamera handler
    }
  }, [showCamera]);

  // Auto-capture a frame for face verification once the video is ready
  useEffect(() => {
    if (!showCamera || cameraTargetField !== 'faceVerification') return;
    let attempts = 0;
    const MAX_ATTEMPTS = 30; // ~6s at 200ms
    const timer = setInterval(() => {
      attempts += 1;
      const video = videoRef.current;
      if (video && video.videoWidth > 0 && video.videoHeight > 0) {
        clearInterval(timer);
        setTimeout(capturePhoto, 500);
      } else if (attempts >= MAX_ATTEMPTS) {
        clearInterval(timer);
        showNotification('Unable to access camera feed for verification.', 'error');
      }
    }, 200);
    return () => clearInterval(timer);
  }, [showCamera, cameraTargetField]);

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      const context = canvas.getContext('2d');
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);
      
      canvas.toBlob((blob) => {
        const filename = `${cameraTargetField}-capture.jpg`;
        const file = new File([blob], filename, { type: 'image/jpeg' });
        handleFileUpload(cameraTargetField, file);
        if (cameraTargetField === 'profilePicture') {
          const url = URL.createObjectURL(blob);
          setProfilePreview(url);
          // Close the camera modal after profile capture
          stopCamera();
        } else {
          const url = URL.createObjectURL(blob);
          setFaceVerificationPreview(url);
          // Trigger verification when live capture is for face verification
          performFaceVerification(file);
          // Do not stop the camera immediately; wait until verification completes
        }
      });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Check email uniqueness when email field changes
    if (field === 'gmail' && value && EMAIL_REGEX.test(value)) {
      checkEmailUniqueness(value);
    } else if (field === 'gmail') {
      setEmailValidationStatus(null);
    }
  };

  // Check if email is unique
  const checkEmailUniqueness = async (email) => {
    if (!email || !EMAIL_REGEX.test(email)) {
      setEmailValidationStatus(null);
      return;
    }

    setEmailValidationStatus('checking');
    
    try {
      const existingLawyerQuery = query(
        collection(db, 'lawyers'),
        where('email', '==', email)
      );
      
      const existingLawyerSnapshot = await getDocs(existingLawyerQuery);
      
      if (!existingLawyerSnapshot.empty) {
        setEmailValidationStatus('duplicate');
      } else {
        setEmailValidationStatus('unique');
      }
    } catch (error) {
      console.error('Error checking email uniqueness:', error);
      setEmailValidationStatus('error');
    }
  };

  const handleFileUpload = (field, file) => {
    if (file && file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      if (field === 'profilePicture') {
        setProfilePreview(url);
      } else if (field === 'faceVerification') {
        setFaceVerificationPreview(url);
        // Perform face verification if both images exist
        if (formData.profilePicture || profilePreview) {
          performFaceVerification(file);
        }
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: file
    }));
  };

  // Clear validation errors when user interacts with any field
  const clearValidationError = (field) => {
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Clear all validation errors
  const clearAllValidationErrors = () => {
    if (Object.values(fieldErrors).some(Boolean)) {
      setFieldErrors({});
    }
  };

  // Handle any form interaction to clear errors
  const handleFormInteraction = () => {
    clearAllValidationErrors();
  };

  const performFaceVerification = async (verificationFile) => {
    // Ensure we have both images: profilePicture file or generated preview URL
    const baseProfile = formData.profilePicture;
    if (!baseProfile && !profilePreview) {
      showNotification('Please upload or take a profile photo first.', 'error');
      return;
    }
    
    setFaceVerificationMatch('verifying');
    showNotification('Verifying face match with AI...', 'success');
    
    try {
      // If we only have a preview URL for profile, fetch it to blob/file
      let profileFile = baseProfile;
      if (!profileFile && profilePreview) {
        const res = await fetch(profilePreview);
        const blob = await res.blob();
        profileFile = new File([blob], 'profile-preview.jpg', { type: blob.type || 'image/jpeg' });
      }
      
      console.log('Starting face verification with Gemini AI...');
      const result = await verifyFaceMatch(profileFile, verificationFile);
      
      if (!result.isHuman) {
        setFaceVerificationMatch('failed');
        showNotification('No human face detected in one or both images. Please ensure clear, well-lit photos of faces.', 'error');
        return;
      }
      
      if (result.isSamePerson) {
        setFaceVerificationMatch('success');
        showNotification('✅ Face verification successful! Your identity has been verified.', 'success');
      } else {
        setFaceVerificationMatch('failed');
        showNotification('❌ Face verification failed. Try taking photos with similar lighting and face positioning. Ensure your face is clearly visible in both images.', 'error');
      }
    } catch (e) {
      console.error('Face verification error:', e);
      setFaceVerificationMatch('failed');
      showNotification('Verification failed. Please try again with clearer photos.', 'error');
    }
    
    // Now that verification has finished, close the camera if open
    if (showCamera) {
      stopCamera();
    }
  };

  const addCustomLocation = () => {
    if (customLocation.trim()) {
      handleInputChange('location', customLocation.trim());
      setCustomLocation('');
      setShowCustomLocation(false);
    }
  };

  const addCustomSpecialization = () => {
    if (customSpecialization.trim() && !selectedSpecializations.includes(customSpecialization.trim())) {
      const newSpecializations = [...selectedSpecializations, customSpecialization.trim()];
      setSelectedSpecializations(newSpecializations);
      handleInputChange('specialization', newSpecializations);
      setCustomSpecialization('');
      setShowCustomSpecialization(false);
    }
  };

  const removeSpecialization = (spec) => {
    const newSpecializations = selectedSpecializations.filter(s => s !== spec);
    setSelectedSpecializations(newSpecializations);
    handleInputChange('specialization', newSpecializations);
  };

  const toggleSpecialization = (spec) => {
    const newSpecializations = selectedSpecializations.includes(spec)
      ? selectedSpecializations.filter(s => s !== spec)
      : [...selectedSpecializations, spec];
    setSelectedSpecializations(newSpecializations);
    handleInputChange('specialization', newSpecializations);
  };

  const toggleLanguage = (lang) => {
    const newLanguages = selectedLanguages.includes(lang)
      ? selectedLanguages.filter(l => l !== lang)
      : [...selectedLanguages, lang];
    setSelectedLanguages(newLanguages);
    handleInputChange('languages', newLanguages);
  };

  const addCustomDegree = () => {
    if (customDegree.trim()) {
      handleInputChange('degree', customDegree.trim());
      setCustomDegree('');
      setShowCustomDegree(false);
    }
  };

  const toggleDay = (day) => {
    console.log('Toggling day:', day);
    console.log('Current selectedDays:', selectedDays);
    
    const newDays = selectedDays.includes(day)
      ? selectedDays.filter(d => d !== day)
      : [...selectedDays, day];
    
    console.log('New selectedDays:', newDays);
    setSelectedDays(newDays);
    handleInputChange('availableDays', newDays);
  };

  const toggleTimeSlot = (slot) => {
    const newTimeSlots = timeSlots.includes(slot)
      ? timeSlots.filter(t => t !== slot)
      : [...timeSlots, slot];
    setTimeSlots(newTimeSlots);
    handleInputChange('timeSlots', newTimeSlots);
  };

  const showNotification = (message, type = 'success') => {
    setShowToast({ message, type });
    setTimeout(() => setShowToast(''), 3000);
  };

  // Function to check localStorage usage
  const checkStorageUsage = () => {
    try {
      let totalSize = 0;
      for (let key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          totalSize += localStorage[key].length;
        }
      }
      console.log('Current localStorage usage:', totalSize, 'characters');
      return totalSize;
    } catch (error) {
      console.error('Error checking storage usage:', error);
      return 0;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submission started');
    setIsSubmitting(true);
    
    // Check storage usage before proceeding
    const currentUsage = checkStorageUsage();
    if (currentUsage > 5000000) { // 5MB threshold
      console.warn('Storage usage is high, clearing some space...');
      clearStorageSpace();
    }

    // Block submit if any inline validation is active
    if (Object.values(fieldErrors).some(Boolean)) {
      showNotification('Please fix the highlighted fields', 'error');
      setIsSubmitting(false);
      return;
    }

    // Validate email format (required field)
    if (!formData.gmail || !EMAIL_REGEX.test(formData.gmail)) {
      console.log('Email validation failed:', formData.gmail);
      showNotification('Please enter a valid email address', 'error');
      setIsSubmitting(false);
      return;
    }

    // Check if a lawyer profile already exists with this email
    if (emailValidationStatus === 'duplicate') {
      showNotification('❌ A lawyer profile already exists with this email address. Only one profile per email is allowed.', 'error');
      setIsSubmitting(false);
      return;
    }

    // Double-check email uniqueness before proceeding
    try {
      console.log('🔍 Double-checking for existing lawyer profile with email:', formData.gmail);
      const existingLawyerQuery = query(
        collection(db, 'lawyers'),
        where('email', '==', formData.gmail)
      );
      
      const existingLawyerSnapshot = await getDocs(existingLawyerQuery);
      
      if (!existingLawyerSnapshot.empty) {
        console.log('❌ Lawyer profile already exists with this email');
        showNotification('❌ A lawyer profile already exists with this email address. Only one profile per email is allowed.', 'error');
        setIsSubmitting(false);
        return;
      }
      
      console.log('✅ No existing lawyer profile found with this email');
    } catch (error) {
      console.error('Error checking for existing lawyer profile:', error);
      // Continue with submission even if check fails
    }
    
    // Validate URL formats for social media (if provided)
    if (formData.linkedin && !URL_REGEX.test(formData.linkedin)) {
      showNotification('Please enter a valid LinkedIn URL', 'error');
      setIsSubmitting(false);
      return;
    }
    
    if (formData.facebook && !URL_REGEX.test(formData.facebook)) {
      showNotification('Please enter a valid Facebook URL', 'error');
      setIsSubmitting(false);
      return;
    }
    
    if (formData.instagram && !URL_REGEX.test(formData.instagram)) {
      showNotification('Please enter a valid Instagram URL', 'error');
      setIsSubmitting(false);
      return;
    }
    
    if (!BAR_LICENSE_REGEX.test(formData.barCouncilLicense || '')) {
      showNotification('Please enter a valid Bar Council License (STATE/NUMBER/YEAR)', 'error');
      setIsSubmitting(false);
      return;
    }

    if (selectedSpecializations.length === 0) {
      showNotification('Please select at least one specialization', 'error');
      setIsSubmitting(false);
      return;
    }

    if (selectedLanguages.length === 0) {
      showNotification('Please select at least one language', 'error');
      setIsSubmitting(false);
      return;
    }

    // Validate experience section
    if (experiences.length === 0) {
      showNotification('Please add at least one experience entry', 'error');
      setIsSubmitting(false);
      return;
    }

    // Check if any experience has years of practice
    const hasYearsOfPractice = experiences.some(exp => exp.yearsOfPractice && exp.yearsOfPractice.trim() !== '');
    if (!hasYearsOfPractice) {
      showNotification('Please enter years of practice in at least one experience entry', 'error');
      setIsSubmitting(false);
      return;
    }

    // Validate education section
    if (education.length === 0) {
      showNotification('Please add at least one education entry', 'error');
      setIsSubmitting(false);
      return;
    }

    // Check if any education has qualification and college
    const hasValidEducation = education.some(edu => edu.qualification && edu.degreeCollege);
    if (!hasValidEducation) {
      showNotification('Please fill in qualification and college for at least one education entry', 'error');
      setIsSubmitting(false);
      return;
    }

    if (!profilePreview) {
      showNotification('Please upload a profile picture', 'error');
      setIsSubmitting(false);
      return;
    }

    // Validate all required fields except LinkedIn, Facebook, Twitter, and Live Face Verification
    const requiredFields = [
      'fullName', 'mobileNumber', 'location', 'professionalBio', 'barCouncilLicense', 
      'gmail', 'consultationFee', 'textConsultationFee', 'chamberAddress'
    ];
    
    const missingRequiredFields = requiredFields.filter(field => !formData[field]);

    if (missingRequiredFields.length > 0) {
      console.log('Missing required fields:', missingRequiredFields);
      showNotification(`Please fill in required fields: ${missingRequiredFields.join(', ')}`, 'error');
      setIsSubmitting(false);
      return;
    }

    // Validate ID Proof (required)
    if (!formData.idProofType) {
      showNotification('Please select ID proof type', 'error');
      setIsSubmitting(false);
      return;
    }

    if (!formData.idProof) {
      showNotification('Please upload ID proof document', 'error');
      setIsSubmitting(false);
      return;
    }

    // Validate Bar Council Certificate (required)
    if (!formData.barCouncilCertificate) {
      showNotification('Please upload Bar Council Certificate', 'error');
      setIsSubmitting(false);
      return;
    }

    // Validate Selfie with ID (required)
    if (!formData.selfieWithId) {
      showNotification('Please upload selfie holding ID for verification', 'error');
      setIsSubmitting(false);
      return;
    }

    // Validate Bank Details (required)
    if (!formData.accountNumber) {
      showNotification('Please enter account number', 'error');
      setIsSubmitting(false);
      return;
    }

    if (!formData.accountHolderName) {
      showNotification('Please enter account holder name', 'error');
      setIsSubmitting(false);
      return;
    }

    if (!formData.bankName) {
      showNotification('Please enter bank name', 'error');
      setIsSubmitting(false);
      return;
    }

    if (!formData.ifscCode) {
      showNotification('Please enter IFSC code', 'error');
      setIsSubmitting(false);
      return;
    }

    // Validate Terms Agreement (required for legal compliance)
    if (!formData.termsAgreement) {
      showNotification('Please agree to terms and conditions', 'error');
      setIsSubmitting(false);
      return;
    }

    // Optional validation warnings (not blocking) - only for truly optional fields
    const optionalWarnings = [];
    
    // Live Face Verification is optional
    if (!faceVerificationMatch || faceVerificationMatch === 'not_attempted') {
      optionalWarnings.push('Live Face Verification (optional)');
    }
    
    // Social media fields are optional
    if (!formData.linkedin) {
      optionalWarnings.push('LinkedIn (optional)');
    }
    
    if (!formData.facebook) {
      optionalWarnings.push('Facebook (optional)');
    }
    
    if (!formData.twitter) {
      optionalWarnings.push('Twitter (optional)');
    }

    // Show warnings if any optional fields are missing
    if (optionalWarnings.length > 0) {
      console.log('Optional fields missing:', optionalWarnings);
      showNotification(`Note: ${optionalWarnings.join(', ')} can be added later`, 'info');
    }

    // Store optional warnings for success message
    const optionalWarningsForSuccess = [...optionalWarnings];

    // Temporarily make face verification optional for testing
    if (faceVerificationMatch !== 'success') {
      console.log('Face verification status:', faceVerificationMatch);
      console.log('⚠️ Face verification failed, but continuing for testing purposes...');
      showNotification('⚠️ Face verification not completed, but continuing with profile creation for testing...', 'success');
      // showNotification('Please complete face verification successfully', 'error');
      // setIsSubmitting(false);
      // return;
    }

    console.log('Selected days:', selectedDays);
    console.log('Form data available days:', formData.availableDays);
    
    if (selectedDays.length === 0) {
      showNotification('Please select at least one available day', 'error');
      setIsSubmitting(false);
      return;
    }

    // Convert profile picture to base64 for storage
    let profilePicBase64 = null;
    if (profilePreview) {
      try {
        const response = await fetch(profilePreview);
        const blob = await response.blob();
        const reader = new FileReader();
        profilePicBase64 = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error('Failed to convert profile picture to base64:', error);
      }
    }

    // Convert ID proof to base64 for storage
    let idProofBase64 = null;
    if (formData.idProof) {
      try {
        const reader = new FileReader();
        idProofBase64 = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(formData.idProof);
        });
      } catch (error) {
        console.error('Failed to convert ID proof to base64:', error);
      }
    }

    // Convert bar council certificate to base64 for storage
    let barCouncilCertBase64 = null;
    if (formData.barCouncilCertificate) {
      try {
        const reader = new FileReader();
        barCouncilCertBase64 = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(formData.barCouncilCertificate);
        });
      } catch (error) {
        console.error('Failed to convert bar council certificate to base64:', error);
      }
    }

    // Convert selfie with ID to base64 for storage
    let selfieWithIdBase64 = null;
    if (formData.selfieWithId) {
      try {
        const reader = new FileReader();
        selfieWithIdBase64 = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(formData.selfieWithId);
        });
      } catch (error) {
        console.error('Failed to convert selfie with ID to base64:', error);
      }
    }



    // Save lawyer profile to Firebase
    try {
      console.log('Creating new lawyer profile for Firebase...');
      
      // Prepare optimized lawyer data for Firebase with reduced size
      const lawyerData = {
        // Basic Info
        full_name: formData.fullName || '',
        email: formData.gmail || '',
        phone: formData.mobileNumber || '',
        bio: formData.professionalBio || '',
        
        // Professional Details
        specialization: selectedSpecializations || [],
        experience_years: experiences.length > 0 ? Number(experiences[0].yearsOfPractice) || 0 : 0,
        bar_council_number: formData.barCouncilLicense || '',
        bar_council_state: formData.location || '', // Location field
        languages: selectedLanguages || [],
        
        // Education (simplified)
        education: education.length > 0 ? education.map(edu => ({
          q: edu.qualification || '', // shortened key
          c: edu.degreeCollege || '', // shortened key
          y: edu.yearOfPassing || '' // shortened key
        })) : [],
        year_of_passing: education.length > 0 ? education[0].yearOfPassing || '' : '', // Separate year of passing field
        
        // Availability
        available_days: selectedDays || [],
        time_slots: timeSlots || {},
        
        // Social Media (properly mapped)
        social: {
          linkedin: formData.linkedin || '',
          facebook: formData.facebook || '',
          instagram: formData.instagram || '',
          twitter: formData.twitter || '',
          email: formData.gmail || ''
        },
        
        // Address
        chamber_address: formData.chamberAddress || '',
        
        // Fees (consolidated)
        fees: {
          consultation: Number(formData.consultationFee) || 0,
          text: Number(formData.textConsultationFee) || 0,
          call: Number(formData.callConsultationFee) || 0,
          video: Number(formData.videoCallConsultationFee) || 0,
          in_person: Number(formData.inPersonConsultationFee) || 0,
          emergency: Number(formData.emergencyConsultationFee) || 0
        },
        emergency_available: formData.emergencyConsultationAvailable || false,
        
        // Achievements (simplified)
        achievements: formData.achievements || '',
        
        // Experience (detailed with all fields)
        experiences: experiences.length > 0 ? experiences.map(exp => ({
          yearsOfPractice: exp.yearsOfPractice || 0,
          firm: exp.firm || '',
          position: exp.position || '',
          subPracticeArea: exp.subPracticeArea || '', // Sub practice area
          casesHandled: exp.casesHandled || 0, // Number of cases handled
          description: exp.description || ''
        })) : [],
        
        // Verification
        face_verification_status: faceVerificationMatch || 'not_attempted',
        verified: faceVerificationMatch === 'success',
        
        // Documents (will be replaced with URLs by the service)
        id_proof_type: formData.idProofType || '',
        profile_pic: profilePicBase64 || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face', // Provide fallback
        id_proof: idProofBase64 || '',
        bar_council_certificate: barCouncilCertBase64 || '',
        selfie_with_id: selfieWithIdBase64 || '',
        face_verification_image: faceVerificationPreview || '',
        government_id: formData.governmentId || '',
        
        // Bank Details (simplified)
        bank: {
          acc: formData.accountNumber || '', // shortened key
          name: formData.accountHolderName || '', // shortened key
          bank: formData.bankName || '', // shortened key
          ifsc: formData.ifscCode || '' // shortened key
        },
        
        // File Reading Charges (simplified)
        file_charges: formData.fileReadingCharges || [],
        
        // Terms
        terms_agreed: formData.termsAgreement || false
      };

      // Save lawyer profile to Firebase
      const lawyerProfileData = {
        ...lawyerData,
        created_at: new Date().toISOString(),
        status: 'pending_approval',
        role: 'lawyer',
        // Admin verification status
        verificationStatus: 'pending', // pending, approved, rejected
        verificationDate: null,
        verificationNotes: '',
        verifiedBy: null
      };
      
      // Clean the data by removing undefined values and handling nested objects
      const cleanData = (obj) => {
        if (obj === null || obj === undefined) return null;
        if (typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) {
          return obj.map(cleanData).filter(item => item !== null);
        }
        const cleaned = {};
        for (const [key, value] of Object.entries(obj)) {
          const cleanedValue = cleanData(value);
          if (cleanedValue !== null && cleanedValue !== undefined) {
            cleaned[key] = cleanedValue;
          }
        }
        return cleaned;
      };
      
      const cleanLawyerData = cleanData(lawyerProfileData);
      
      // Import Firebase service
      const { lawyerService } = await import('../services/firebaseService');
      
      // Use the actual authenticated user's UID instead of a temporary ID
      const actualUserId = currentUser?.uid;
      if (!actualUserId) {
        console.error('❌ No authenticated user found. Cannot create lawyer profile.');
        showNotification('Authentication error. Please sign in first.', 'error');
        setIsSubmitting(false);
        return;
      }
      
      const lawyerProfileWithId = {
        ...cleanLawyerData,
        uid: actualUserId, // Use actual Firebase Auth UID
        profileComplete: true, // Mark profile as complete
        isVerified: false,
        verificationStatus: 'pending',
        status: 'pending_approval'
      };
      
      console.log('🎯 Creating lawyer profile with UID:', actualUserId);
      console.log('🎯 Lawyer profile data:', lawyerProfileWithId);
      
      console.log('Cleaned lawyer data for Firebase:', lawyerProfileWithId);
      
      // Additional validation - check for any remaining undefined values
      const hasUndefinedValues = JSON.stringify(lawyerProfileWithId).includes('undefined');
      if (hasUndefinedValues) {
        console.error('WARNING: Data still contains undefined values!');
        console.log('Raw data:', JSON.stringify(lawyerProfileWithId, null, 2));
      }
      
      // Save to Firebase
      console.log('Attempting to save to Firebase...');
      const result = await lawyerService.createLawyer(lawyerProfileWithId);
      
      if (result.success) {
        console.log('Lawyer profile saved to Firebase:', result.data);
        
        // Store in localStorage for reference (but clear the old pending profile)
        localStorage.removeItem('pendingLawyerProfile'); // Clear old data first
        localStorage.setItem('pendingLawyerProfile', JSON.stringify(lawyerProfileWithId));
        localStorage.setItem('actualLawyerId', actualUserId);
        
        // Also save to lawyers array in localStorage for settings access
        try {
          const existingLawyers = JSON.parse(localStorage.getItem('lawyers') || '[]');
          const lawyerForSettings = {
            ...lawyerProfileWithId,
            userId: actualUserId, // Use actual Firebase Auth UID
            // Basic Information
            profilePicture: lawyerProfileWithId.profile_pic,
            fullName: lawyerProfileWithId.full_name,
            mobileNumber: lawyerProfileWithId.phone,
            location: lawyerProfileWithId.bar_council_state,
            professionalBio: lawyerProfileWithId.bio,
            barCouncilLicense: lawyerProfileWithId.bar_council_number,
            specialization: lawyerProfileWithId.specialization,
            yearsOfPractice: lawyerProfileWithId.experience_years,
            education: lawyerProfileWithId.education,
            yearOfPassing: lawyerProfileWithId.year_of_passing || '',
            chamberAddress: lawyerProfileWithId.chamber_address,
            languages: lawyerProfileWithId.languages,
            achievements: lawyerProfileWithId.achievements,
            experiences: lawyerProfileWithId.experiences || [],
            
            // Contact Information
            gmail: lawyerProfileWithId.email,
            linkedin: lawyerProfileWithId.social?.linkedin || '',
            facebook: lawyerProfileWithId.social?.facebook || '',
            instagram: lawyerProfileWithId.social?.instagram || '',
            twitter: lawyerProfileWithId.social?.twitter || '',
            
            // Consultation Fees
            textConsultationFee: lawyerProfileWithId.fees?.text || '',
            callConsultationFee: lawyerProfileWithId.fees?.call || '',
            videoCallConsultationFee: lawyerProfileWithId.fees?.video || '',
            inPersonConsultationFee: lawyerProfileWithId.fees?.in_person || '',
            emergencyConsultationAvailable: lawyerProfileWithId.emergency_available || false,
            emergencyConsultationFee: lawyerProfileWithId.fees?.emergency || '',
            
            // Availability
            availableDays: lawyerProfileWithId.available_days,
            timeSlots: lawyerProfileWithId.time_slots,
            
            // Documents
            idProofType: lawyerProfileWithId.id_proof_type,
            idProof: lawyerProfileWithId.id_proof,
            barCouncilCertificate: lawyerProfileWithId.bar_council_certificate,
            selfieWithId: lawyerProfileWithId.selfie_with_id,
            
            // File Reading Charges
            fileReadingCharges: lawyerProfileWithId.file_charges,
            
            // Bank Details
            accountNumber: lawyerProfileWithId.bank?.acc || '',
            accountHolderName: lawyerProfileWithId.bank?.name || '',
            bankName: lawyerProfileWithId.bank?.bank || '',
            ifscCode: lawyerProfileWithId.bank?.ifsc || '',
            
            // Additional metadata
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            status: 'pending_approval',
            isVerified: false
          };
          
          // Remove any existing lawyer with same actual ID
          const filteredLawyers = existingLawyers.filter(l => l.userId !== actualUserId);
          filteredLawyers.push(lawyerForSettings);
          
          localStorage.setItem('lawyers', JSON.stringify(filteredLawyers));
          console.log('✅ Lawyer profile saved to localStorage for settings access');
        } catch (error) {
          console.error('Error saving lawyer profile to localStorage:', error);
        }
        
        // Clear saved draft on success
        try {
          localStorage.removeItem(DRAFT_KEY);
        } catch (e) {
          console.warn('Could not remove draft:', e);
        }
        
        console.log('Profile submitted for admin approval!');
        // Show success message
        setShowSuccessMessage(true);
        setIsSubmitting(false);
        
        // Show success message
        const hasOptionalFields = optionalWarningsForSuccess.length === 0;
        const successMessage = hasOptionalFields 
          ? '✅ Profile created successfully! Your profile is under review.'
          : '✅ Profile created successfully! Optional fields can be added later. Your profile is under review.';
        showNotification(successMessage, 'success');
        
        // Set the role as lawyer in localStorage before redirecting
        localStorage.setItem('selectedRole', 'lawyer');
        
        // Set flag to prevent reloading old profile data
        sessionStorage.setItem('profileJustCreated', 'true');
        
        // Set flag that lawyer profile is created (for auth navigation)
        localStorage.setItem('lawyerProfileCreated', 'true');
        
        // Set flag that lawyer profile is complete to prevent redirect back to profile creation
        localStorage.setItem('isProfileComplete', 'true');
        
        // Start countdown timer with more reliable approach
        let timeLeft = 10;
        console.log('Starting countdown timer...');
        
        // Use a more reliable timer approach
        const startCountdown = () => {
          const countdownInterval = setInterval(() => {
            timeLeft -= 1;
            console.log('Countdown:', timeLeft);
            setCountdown(timeLeft);
            
            if (timeLeft <= 0) {
              clearInterval(countdownInterval);
              window.countdownInterval = null;
              console.log('Countdown finished, navigating to /pending-approval');
              // Clear the pending profile data to prevent reloading on next visit
              localStorage.removeItem('pendingLawyerProfile');
              sessionStorage.removeItem('profileJustCreated');
              // Force navigation directly to pending approval screen
              window.location.href = '/pending-approval';
            }
          }, 1000);
          
          // Store interval reference globally for cleanup
          window.countdownInterval = countdownInterval;
        };
        
        // Start countdown after a small delay to ensure state is updated
        setTimeout(startCountdown, 100);
        
        // Also set a flag to indicate profile is complete for AuthPage navigation
        localStorage.setItem('lawyerProfileComplete', 'true');
      } else {
        console.error('Failed to save to Firebase:', result.error);
        
        // Check if it's a duplicate email error
        if (result.error.includes('already exists with this email')) {
          showNotification('❌ A lawyer profile already exists with this email address. Only one profile per email is allowed.', 'error');
        } else if (result.error.includes('network') || result.error.includes('ERR_BLOCKED_BY_CLIENT')) {
          showNotification('Network error: Please check your internet connection and disable ad blockers for this site.', 'error');
        } else {
          showNotification('Failed to save profile to database. Please try again.', 'error');
        }
        setIsSubmitting(false);
      }
      
    } catch (error) {
      console.error('Error creating profile:', error);
      showNotification('Failed to save profile locally.', 'error');
      setIsSubmitting(false);
    }
  };

  // Derive a simple UI status for verification badge
  const verificationStatus = (() => {
    if (faceVerificationMatch === 'verifying') {
      return { label: 'Verifying…', classes: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' };
    }
    if (faceVerificationMatch === 'success') {
      return { label: 'Face verified', classes: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' };
    }
    if (faceVerificationMatch === 'failed') {
      return { label: 'Verification failed', classes: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' };
    }
    return { label: 'Not verified', classes: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' };
  })();

  return (
    <div 
      className="min-h-screen bg-gray-50 dark:bg-gray-900"
      onClick={handleFormInteraction}
      onTouchStart={handleFormInteraction}
    >
      {/* Toast Notification */}
      {showToast && (
        <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 px-4 py-2 rounded-lg text-white font-medium ${showToast.type === 'error' ? 'bg-red-500' : 'bg-green-500'}`}>
          {showToast.message}
        </div>
      )}

      {/* Success Message Modal */}
      {showSuccessMessage && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-2xl transform transition-all duration-300 scale-100">
            {/* Success Icon */}
            <div className="mx-auto mb-6 w-20 h-20 bg-gradient-to-r from-green-400 to-green-600 rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            
            {/* Success Title */}
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Profile Created Successfully! 🎉
            </h2>
            
            {/* Success Message */}
            <div className="text-gray-600 mb-6 space-y-3">
              <p className="text-lg">
                Your lawyer profile has been created!
              </p>
              <p className="text-sm">
                Your profile is under verification. It will be verified by our team soon. Please be patient, thank you!
              </p>
              <p className="text-sm">
                You'll be redirected to the pending approval page where you can track your verification status.
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-3">
                <p className="text-yellow-800 text-xs">
                  💡 <strong>Tip:</strong> If you see Firebase errors in the console, try disabling ad blockers for this site.
                </p>
              </div>
            </div>
            
            {/* Redirecting Message */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="text-blue-700 text-sm font-medium">
                  Redirecting to pending approval page in <span className="font-bold">{countdown}</span> seconds...
                </span>
              </div>
            </div>
            
            {/* Continue Button */}
            <button
              onClick={() => {
                // Clear any existing countdown timer
                if (window.countdownInterval) {
                  clearInterval(window.countdownInterval);
                  window.countdownInterval = null;
                }
                console.log('Manual navigation to /pending-approval');
                // Force navigation using window.location for reliability
                window.location.href = '/pending-approval';
              }}
              className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Continue to Pending Approval
            </button>
          </div>
        </div>
      )}

      {/* Camera Modal with auto-detect */}
      {showCamera && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {cameraTargetField === 'faceVerification' ? 'Face Verification' : t.takePhoto}
              </h3>
              <button
                onClick={stopCamera}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="relative mb-4">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-64 object-cover rounded-lg"
              />
              <button
                onClick={() => setFlashEnabled(!flashEnabled)}
                className={`absolute top-2 right-2 p-2 rounded-full ${flashEnabled ? 'bg-yellow-500 text-white' : 'bg-gray-700 text-gray-300'}`}
              >
                {flashEnabled ? <Zap className="w-5 h-5" /> : <ZapOff className="w-5 h-5" />}
              </button>
            </div>
            <div className="flex gap-2 justify-end">
              {(cameraTargetField === 'profilePicture' || cameraTargetField === 'selfieWithId') && (
                <button
                  onClick={capturePhoto}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm"
                >
                  {cameraTargetField === 'selfieWithId' ? 'Capture Selfie' : 'Capture Now'}
                </button>
              )}
              <button onClick={stopCamera} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-lg text-sm">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div 
        className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50"
        onClick={handleFormInteraction}
      >
        <div className="px-4 py-4 max-w-md mx-auto">
          <div className="flex items-center">
            <button
              onClick={() => console.log('Go back')}
              className="mr-4 p-1"
            >
              <ArrowLeft className="w-6 h-6 text-gray-600 dark:text-gray-400" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t.title}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {t.subtitle}
              </p>
              <div className={`inline-flex items-center mt-2 px-2 py-1 rounded-full text-xs font-medium ${verificationStatus.classes}`}>
                {verificationStatus.label}
              </div>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Profile Completion
              </span>
              <span className="text-sm font-semibold text-purple-600 dark:text-purple-400">
                {progress}%
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-purple-500 to-purple-600 h-2 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
              <span>0%</span>
              <span>100%</span>
            </div>
          </div>
        </div>
      </div>



      {/* Form Content */}
      <div 
        className="px-4 py-6 space-y-6 max-w-md mx-auto pb-32"
        onClick={handleFormInteraction}
        onTouchStart={handleFormInteraction}
      >
        <form 
          onSubmit={handleSubmit} 
          className="space-y-6"
          onClick={handleFormInteraction}
        >
          {/* Profile Picture Section */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex flex-col items-center">
              <div className="relative mb-4">
                {profilePreview ? (
                  <img
                    src={profilePreview}
                    alt="Profile Preview"
                    className="w-24 h-24 rounded-full object-cover border-4 border-purple-200 dark:border-purple-700"
                  />
                ) : (
                  <div className="w-24 h-24 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-full flex items-center justify-center bg-gray-50 dark:bg-gray-700">
                    <User className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                <canvas ref={canvasRef} className="hidden" />
              </div>
              
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={() => startCamera('profilePicture')}
                  className="px-4 py-2 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-lg text-sm font-medium hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors flex items-center"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  {t.takePhoto}
                </button>
                
                <label className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors cursor-pointer flex items-center">
                  <Upload className="w-4 h-4 mr-2" />
                  {t.uploadFromPhone}
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => handleFileUpload('profilePicture', e.target.files[0])}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => startCamera('faceVerification')}
                  className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors cursor-pointer flex items-center"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  {t.liveFaceVerification}
                  {GEMINI_API_KEY && (
                    <span className="ml-1 text-xs text-purple-600 dark:text-purple-400">(AI)</span>
                  )}
                </button>
              </div>

              {/* Verification status + preview */}
              {(faceVerificationPreview || faceVerificationMatch) && (
                <div className="mt-3 flex items-center gap-3">
                  {faceVerificationPreview && (
                    <img
                      src={faceVerificationPreview}
                      alt="Verification preview"
                      className="w-12 h-12 rounded-full object-cover border border-gray-200 dark:border-gray-700"
                    />
                  )}
                  {faceVerificationMatch === 'verifying' && (
                    <span className="text-sm text-gray-600 dark:text-gray-300 flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mr-2"></div>
                      Verifying with AI…
                    </span>
                  )}
                  {faceVerificationMatch === 'success' && (
                    <span className="text-sm text-green-600 flex items-center"><CheckCircle className="w-4 h-4 mr-1"/> Face verified</span>
                  )}
                  {faceVerificationMatch === 'failed' && (
                    <div className="flex flex-col items-center">
                      <span className="text-sm text-red-600 flex items-center mb-2">
                        <AlertCircle className="w-4 h-4 mr-1"/> Verification failed
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setFaceVerificationMatch(null);
                          setFaceVerificationPreview(null);
                          startCamera('faceVerification');
                        }}
                        className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 underline"
                      >
                        Try again with better lighting
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Basic Information */}
          <div className="space-y-4">
            {/* Full Name */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <User className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-3" />
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleInputChange('fullName', value);
                    if (value && !/^[A-Za-z\s]+$/.test(value)) {
                      setValidationError('fullName', 'Only letters and spaces are allowed');
                    } else {
                      setValidationError('fullName', '');
                    }
                  }}
                  placeholder={t.fullNamePlaceholder}
                  className={`flex-1 bg-transparent border-none outline-none text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${fieldErrors.fullName ? 'ring-1 ring-red-500 rounded' : ''}`}
                />
              </div>
              {fieldErrors.fullName && (
                <p className="text-xs text-red-600 mt-2">{fieldErrors.fullName}</p>
              )}
            </div>

            {/* Mobile Number (Private) */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <Phone className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-3" />
                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={formData.mobileNumber}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleInputChange('mobileNumber', value);
                    if (value && /[^0-9]/.test(value)) {
                      setValidationError('mobileNumber', 'Only digits (0-9) are allowed');
                    } else {
                      setValidationError('mobileNumber', '');
                    }
                  }}
                  placeholder="Mobile Number (Private, admin-only)"
                  className={`flex-1 bg-transparent border-none outline-none text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${fieldErrors.mobileNumber ? 'ring-1 ring-red-500 rounded' : ''}`}
                />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">This number is stored but not shown to users.</p>
              {fieldErrors.mobileNumber && (
                <p className="text-xs text-red-600 mt-2">{fieldErrors.mobileNumber}</p>
              )}
            </div>

            {/* Location */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <MapPin className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-3" />
                <div className="flex-1">
                  {!showCustomLocation ? (
                    <div className="flex items-center">
                      <select
                        value={formData.location}
                        onChange={(e) => {
                          if (e.target.value === 'custom') {
                            setShowCustomLocation(true);
                          } else {
                            handleInputChange('location', e.target.value);
                          }
                        }}
                        className="flex-1 bg-transparent border-none outline-none text-gray-900 dark:text-white"
                      >
                        <option value="">{t.locationPlaceholder}</option>
                        {indianCities.map(city => (
                          <option key={city} value={city}>{city}</option>
                        ))}
                        <option value="custom">{t.addCustomLocation}</option>
                      </select>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={customLocation}
                        onChange={(e) => setCustomLocation(e.target.value)}
                        placeholder={t.customLocationPlaceholder}
                        className="flex-1 bg-transparent border-none outline-none text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      />
                      <button
                        type="button"
                        onClick={addCustomLocation}
                        className="text-green-600 hover:text-green-700"
                      >
                        <CheckCircle className="w-5 h-5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowCustomLocation(false)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Professional Bio */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-start">
                <FileText className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-3 mt-1" />
                <textarea
                  value={formData.professionalBio}
                  onChange={(e) => handleInputChange('professionalBio', e.target.value)}
                  placeholder={t.bioPlaceholder}
                  rows={3}
                  maxLength={300}
                  className="flex-1 bg-transparent border-none outline-none text-gray-900 dark:text-white resize-none placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 text-right">
                {formData.professionalBio.length}/300
              </p>
            </div>

            {/* Bar Council ID / Registration Number */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <BookOpen className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-3" />
                <div className="flex-1">
                  <div className="text-xs text-gray-800 dark:text-gray-800 mb-1">Bar Council ID / Registration Number</div>
                  <input
                    type="text"
                    value={formData.barCouncilLicense}
                    onChange={(e) => {
                      const raw = e.target.value.toUpperCase();
                      const value = raw.replace(/[^A-Z0-9\/]/g, '');
                      handleInputChange('barCouncilLicense', value);
                      if (value && !BAR_LICENSE_REGEX.test(value)) {
                        setValidationError('barCouncilLicense', 'Format: STATE/1234/2024 (e.g., D/1461/2017 or MAH/1548/2003)');
                      } else {
                        setValidationError('barCouncilLicense', '');
                      }
                    }}
                    onFocus={() => clearValidationError('barCouncilLicense')}
                    placeholder="e.g., MAH/1548/2003"
                    className={`w-full bg-transparent border-none outline-none text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${fieldErrors.barCouncilLicense ? 'ring-1 ring-red-500 rounded' : ''}`}
                    inputMode="text"
                    autoCapitalize="characters"
                  />
                </div>
              </div>
              {fieldErrors.barCouncilLicense && (
                <p className="text-xs text-red-600 mt-1">{fieldErrors.barCouncilLicense}</p>
              )}
            </div>

            {/* Specialization */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="space-y-3">
                <div className="flex items-center">
                  <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-3" />
                  <span className="text-gray-700 dark:text-gray-300 font-medium">{t.specialization}</span>
                </div>
                
                {/* Selected Specializations */}
                {selectedSpecializations.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectedSpecializations.map(spec => (
                      <span
                        key={spec}
                        className="bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 px-3 py-1 rounded-full text-sm flex items-center"
                      >
                        {spec}
                        <button
                          type="button"
                          onClick={() => removeSpecialization(spec)}
                          className="ml-2 text-purple-500 hover:text-purple-700"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Specialization Dropdown */}
                <div className="space-y-2">
                  <select
                    onChange={(e) => {
                      if (e.target.value === 'custom') {
                        setShowCustomSpecialization(true);
                      } else if (e.target.value) {
                        toggleSpecialization(e.target.value);
                        e.target.value = '';
                      }
                    }}
                    className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white"
                  >
                    <option value="">{t.specializationPlaceholder}</option>
                    {legalSpecializations.filter(spec => !selectedSpecializations.includes(spec)).map(spec => (
                      <option key={spec} value={spec}>{spec}</option>
                    ))}
                    <option value="custom">{t.addCustomSpecialization}</option>
                  </select>

                  {/* Custom Specialization Input */}
                  {showCustomSpecialization && (
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={customSpecialization}
                        onChange={(e) => setCustomSpecialization(e.target.value)}
                        placeholder={t.customSpecializationPlaceholder}
                        className="flex-1 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      />
                      <button
                        type="button"
                        onClick={addCustomSpecialization}
                        className="text-green-600 hover:text-green-700"
                      >
                        <CheckCircle className="w-5 h-5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowCustomSpecialization(false)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Experience Section */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Clock className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-3" />
                    <span className="text-gray-700 dark:text-gray-300 font-medium">Experience</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setExperiences([...experiences, {
                        subPracticeArea: '',
                        casesHandled: '',
                        yearsOfPractice: ''
                      }]);
                    }}
                    className="flex items-center text-purple-600 hover:text-purple-700 text-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add More Experience
                  </button>
                </div>

                {experiences.map((exp, index) => (
                  <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Experience {index + 1}</span>
                      {experiences.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newExperiences = experiences.filter((_, i) => i !== index);
                            setExperiences(newExperiences);
                          }}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* Sub Practice Area */}
                    <select
                      value={exp.subPracticeArea}
                      onChange={(e) => {
                        const newExperiences = [...experiences];
                        newExperiences[index].subPracticeArea = e.target.value;
                        setExperiences(newExperiences);
                      }}
                      className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white"
                    >
                      <option value="">Select Sub Practice Area</option>
                      {practiceAreasData.map(practice => 
                        practice.areas.map(area => 
                          area.sub_areas.map(subArea => (
                            <option key={subArea} value={subArea}>{subArea}</option>
                          ))
                        )
                      ).flat().filter((value, index, self) => self.indexOf(value) === index)}
                    </select>

                    {/* Number of Cases Handled */}
                    <input
                      type="number"
                      value={exp.casesHandled}
                      onChange={(e) => {
                        const newExperiences = [...experiences];
                        newExperiences[index].casesHandled = e.target.value;
                        setExperiences(newExperiences);
                      }}
                      placeholder="Number of Cases Handled"
                      className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    />

                    {/* Years of Practice */}
                    <input
                      type="number"
                      value={exp.yearsOfPractice}
                      onChange={(e) => {
                        const newExperiences = [...experiences];
                        newExperiences[index].yearsOfPractice = e.target.value;
                        setExperiences(newExperiences);
                      }}
                      placeholder="Years of Practice"
                      className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    />
                  </div>
                ))}
              </div>
            </div>



            {/* Study Section */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <GraduationCap className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-3" />
                    <span className="text-gray-700 dark:text-gray-300 font-medium">{t.study}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setEducation([...education, {
                        qualification: '',
                        degreeCollege: ''
                      }]);
                    }}
                    className="flex items-center text-purple-600 hover:text-purple-700 text-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {t.addMoreStudy}
                  </button>
                </div>

                {education.map((edu, index) => (
                  <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{t.study} {index + 1}</span>
                      {education.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newEducation = education.filter((_, i) => i !== index);
                            setEducation(newEducation);
                          }}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* Qualification */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t.qualification}
                      </label>
                      {!showCustomQualification ? (
                        <select
                          value={edu.qualification}
                          onChange={(e) => {
                            if (e.target.value === 'custom') {
                              setShowCustomQualification(true);
                            } else {
                              const newEducation = [...education];
                              newEducation[index].qualification = e.target.value;
                              setEducation(newEducation);
                            }
                          }}
                          className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white"
                        >
                          <option value="">{t.qualificationPlaceholder}</option>
                          {lawQualifications.map(qualification => (
                            <option key={qualification} value={qualification}>{qualification}</option>
                          ))}
                          <option value="custom">{t.addCustomQualification}</option>
                        </select>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={customQualification}
                            onChange={(e) => setCustomQualification(e.target.value)}
                            placeholder={t.customQualificationPlaceholder}
                            className="flex-1 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (customQualification.trim()) {
                                const newEducation = [...education];
                                newEducation[index].qualification = customQualification.trim();
                                setEducation(newEducation);
                                setCustomQualification('');
                                setShowCustomQualification(false);
                              }
                            }}
                            className="text-green-600 hover:text-green-700 p-2"
                          >
                            <CheckCircle className="w-5 h-5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowCustomQualification(false);
                              setCustomQualification('');
                            }}
                            className="text-red-600 hover:text-red-700 p-2"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Degree College */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t.degreeCollege}
                      </label>
                      {!showCustomDegree ? (
                        <select
                          value={edu.degreeCollege}
                          onChange={(e) => {
                            if (e.target.value === 'custom') {
                              setShowCustomDegree(true);
                            } else {
                              const newEducation = [...education];
                              newEducation[index].degreeCollege = e.target.value;
                              setEducation(newEducation);
                            }
                          }}
                          className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white"
                        >
                          <option value="">{t.degreeCollegePlaceholder}</option>
                          {lawInstitutions.map(institution => (
                            <option key={institution} value={institution}>{institution}</option>
                          ))}
                          <option value="custom">{t.addCustomCollege}</option>
                        </select>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={customDegree}
                            onChange={(e) => setCustomDegree(e.target.value)}
                            placeholder={t.customCollegePlaceholder}
                            className="flex-1 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (customDegree.trim()) {
                                const newEducation = [...education];
                                newEducation[index].degreeCollege = customDegree.trim();
                                setEducation(newEducation);
                                setCustomDegree('');
                                setShowCustomDegree(false);
                              }
                            }}
                            className="text-green-600 hover:text-green-700 p-2"
                          >
                            <CheckCircle className="w-5 h-5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowCustomDegree(false);
                              setCustomDegree('');
                            }}
                            className="text-red-600 hover:text-red-700 p-2"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Year of Passing */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <Calendar className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-3" />
                <input
                  type="number"
                  min="1950"
                  max={new Date().getFullYear()}
                  value={formData.yearOfPassing}
                  onChange={(e) => handleInputChange('yearOfPassing', e.target.value)}
                  placeholder="Year of Passing"
                  className="flex-1 bg-transparent border-none outline-none text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>
            </div>

            {/* Consultation Fee */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <IndianRupeeIcon className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-3" />
                <span className="text-gray-600 dark:text-gray-300 mr-2"></span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={formData.consultationFee}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleInputChange('consultationFee', value);
                    if (value && /[^0-9]/.test(value)) {
                      setValidationError('consultationFee', 'Numbers only');
                    } else {
                      setValidationError('consultationFee', '');
                    }
                  }}
                  placeholder={t.feePlaceholder}
                  className={`flex-1 bg-transparent border-none outline-none text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${fieldErrors.consultationFee ? 'ring-1 ring-red-500 rounded' : ''}`}
                />
              </div>
              {fieldErrors.consultationFee && (
                <p className="text-xs text-red-600 mt-2">{fieldErrors.consultationFee}</p>
              )}
            </div>



            {/* Languages */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="space-y-3">
                <div className="flex items-center">
                  <BookOpen className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-3" />
                  <span className="text-gray-700 dark:text-gray-300 font-medium">Languages Spoken *</span>
                </div>
                
                {/* Language Selection with Checkboxes */}
                <div className="grid grid-cols-2 gap-2">
                  {indianLanguages.map(lang => (
                    <label key={lang.value} className="flex items-center p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedLanguages.includes(lang.value)}
                        onChange={() => toggleLanguage(lang.value)}
                        className="mr-2 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {language === 'hindi' ? lang.labelHindi : lang.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Chamber Address */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-start">
                <MapPin className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-3 mt-1" />
                <textarea
                  value={formData.chamberAddress}
                  onChange={(e) => handleInputChange('chamberAddress', e.target.value)}
                  placeholder="Enter your chamber address"
                  rows={3}
                  maxLength={500}
                  className="flex-1 bg-transparent border-none outline-none text-gray-900 dark:text-white resize-none placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 text-right">
                {formData.chamberAddress.length}/500
              </p>
            </div>
          </div>

          {/* Identity Verification Section */}
          <div className="space-y-4">
            <div className="px-1">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t.identityVerification}</h3>
                <div className="flex items-center bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 px-2 py-1 rounded-full text-xs font-medium">
                  <EyeOff className="w-3 h-3 mr-1" />
                  Admin Only
                </div>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t.identitySubtitle}</p>
              <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                ⚠️ This information is only visible to administrators and not shown to users
              </p>
            </div>

            {/* ID Proof (Aadhar/PAN/Passport) */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="space-y-3">
                <div className="flex items-center">
                  <CreditCard className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-3" />
                  <span className="text-purple-600 dark:text-purple-400 font-medium">
                    ID Proof (Aadhar/PAN/Passport) *
                  </span>
                </div>
                
                {/* ID Type Selection */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Select ID type
                  </label>
                  <select
                    value={formData.idProofType}
                    onChange={(e) => handleInputChange('idProofType', e.target.value)}
                    className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white"
                  >
                    <option value="">Select ID Type</option>
                    <option value="aadhar">Aadhar Card</option>
                    <option value="pan">PAN Card</option>
                    <option value="passport">Passport</option>
                  </select>
                </div>

                {/* Upload ID Proof */}
                <label className="block cursor-pointer">
                  <div className="flex items-center p-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-purple-400 dark:hover:border-purple-400 transition-colors">
                    <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center mr-4">
                      <Upload className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div className="flex-1">
                      <span className="text-purple-600 dark:text-purple-400 font-medium">
                        Upload ID Proof
                      </span>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {formData.idProofType ? `Upload your ${formData.idProofType.toUpperCase()} card` : 'Select ID type first'}
                      </p>
                    </div>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*,.pdf"
                    onChange={(e) => handleFileUpload('idProof', e.target.files[0])}
                    disabled={!formData.idProofType}
                  />
                </label>

                {/* Upload confirmation */}
                {formData.idProof && (
                  <div className="mt-2">
                    <p className="text-sm text-green-600 dark:text-green-400">
                      ✓ {formData.idProofType.toUpperCase()} uploaded successfully
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formData.idProof.name}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Bar Council Certificate / Practicing License */}
            <label className="block bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center mr-4">
                  <Upload className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="flex-1">
                  <span className="text-purple-600 dark:text-purple-400 font-medium">
                    Bar Council Certificate / Practicing License *
                  </span>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Upload Certificate
                  </p>
                </div>
              </div>
              <input
                type="file"
                className="hidden"
                accept="image/*,.pdf"
                onChange={(e) => handleFileUpload('barCouncilCertificate', e.target.files[0])}
              />
              {formData.barCouncilCertificate && (
                <p className="text-sm text-green-600 dark:text-green-400 mt-2 ml-14">
                  ✓ {formData.barCouncilCertificate.name}
                </p>
              )}
            </label>

            {/* Selfie holding ID (for verification) */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="space-y-3">
                <div className="flex items-center">
                  <Camera className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-3" />
                  <span className="text-purple-600 dark:text-purple-400 font-medium">
                    Selfie holding ID (for verification) *
                  </span>
                </div>
                
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => startCamera('selfieWithId')}
                    className="px-4 py-2 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-lg text-sm font-medium hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors flex items-center"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Take Photo
                  </button>
                  
                  <label className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors cursor-pointer flex items-center">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Photo
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => handleFileUpload('selfieWithId', e.target.files[0])}
                    />
                  </label>
                </div>

                {/* Preview of uploaded selfie */}
                {formData.selfieWithId && (
                  <div className="mt-3">
                    <p className="text-sm text-green-600 dark:text-green-400 mb-2">
                      ✓ Selfie with ID uploaded successfully
                    </p>
                    <img
                      src={URL.createObjectURL(formData.selfieWithId)}
                      alt="Selfie with ID preview"
                      className="w-20 h-20 rounded-lg object-cover border border-gray-200 dark:border-gray-700"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Service Setup Section */}
          <div className="space-y-4">
            {/* Consultation Fees */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="space-y-4">
                <div className="flex items-center">
                  <PiggyBank className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Service Setup</h3>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Configure your consultation fees for different service types</p>
                <div className="space-y-3">
              {/* Text Consultation Fee */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center">
                  <MessageSquare className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-3" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700 dark:text-gray-300 font-medium">Text Consultation Fee *</span>
                      <span className="text-xs text-red-500 font-medium">Required</span>
                    </div>
                    <div className="flex items-center mt-2">
                      <IndianRupeeIcon className="w-4 h-4 text-gray-400 mr-2" />
                      <input
                        type="number"
                        value={formData.textConsultationFee}
                        onChange={(e) => handleInputChange('textConsultationFee', e.target.value)}
                        placeholder="Enter amount"
                        className="flex-1 bg-transparent border-none outline-none text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Call Consultation Fee */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center">
                  <Phone className="w-5 h-5 text-purple-600 mr-3" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700 dark:text-gray-300 font-medium">Call Consultation Fee</span>
                      <span className="text-xs text-gray-400 font-medium">Optional</span>
                    </div>
                    <div className="flex items-center mt-2">
                      <IndianRupeeIcon className="w-4 h-4 text-gray-400 mr-2" />
                      <input
                        type="number"
                        value={formData.callConsultationFee}
                        onChange={(e) => handleInputChange('callConsultationFee', e.target.value)}
                        placeholder="Enter amount"
                        className="flex-1 bg-transparent border-none outline-none text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Video Call Consultation Fee */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center">
                  <Video className="w-5 h-5 text-purple-600 mr-3" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700 dark:text-gray-300 font-medium">Video Call Consultation Fee</span>
                      <span className="text-xs text-gray-400 font-medium">Optional</span>
                    </div>
                    <div className="flex items-center mt-2">
                      <IndianRupeeIcon className="w-4 h-4 text-gray-400 mr-2" />
                      <input
                        type="number"
                        value={formData.videoCallConsultationFee}
                        onChange={(e) => handleInputChange('videoCallConsultationFee', e.target.value)}
                        placeholder="Enter amount"
                        className="flex-1 bg-transparent border-none outline-none text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* In Person Consultation Fee */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center">
                  <User className="w-5 h-5 text-purple-600 mr-3" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700 dark:text-gray-300 font-medium">In Person Consultation Fee</span>
                      <span className="text-xs text-gray-400 font-medium">Optional</span>
                    </div>
                    <div className="flex items-center mt-2">
                      <IndianRupeeIcon className="w-4 h-4 text-gray-400 mr-2" />
                      <input
                        type="number"
                        value={formData.inPersonConsultationFee}
                        onChange={(e) => handleInputChange('inPersonConsultationFee', e.target.value)}
                        placeholder="Enter amount"
                        className="flex-1 bg-transparent border-none outline-none text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      />
                    </div>
                  </div>
                </div>
              </div>
                </div>
              </div>
            </div>
          </div>

          {/* Availability Section */}
          <div className="space-y-4">
            {/* Available Days Container */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="space-y-4">
                <div className="flex items-center">
                  <Calendar className="w-5 h-5 text-purple-600 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Availability (days & time slots)</h3>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Select your available days and time slots for consultations</p>
                
                {/* Available Days Selection */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Available Days</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { value: 'monday', label: 'Monday' },
                      { value: 'tuesday', label: 'Tuesday' },
                      { value: 'wednesday', label: 'Wednesday' },
                      { value: 'thursday', label: 'Thursday' },
                      { value: 'friday', label: 'Friday' },
                      { value: 'saturday', label: 'Saturday' },
                      { value: 'sunday', label: 'Sunday' }
                    ].map((day) => (
                      <label key={day.value} className="flex items-center p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedDays.includes(day.value)}
                          onChange={() => toggleDay(day.value)}
                          className="mr-2 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {day.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              <div className="space-y-3">
              {selectedDays.map((dayValue) => {
                const day = [
                  { value: 'monday', label: 'Monday' },
                  { value: 'tuesday', label: 'Tuesday' },
                  { value: 'wednesday', label: 'Wednesday' },
                  { value: 'thursday', label: 'Thursday' },
                  { value: 'friday', label: 'Friday' },
                  { value: 'saturday', label: 'Saturday' },
                  { value: 'sunday', label: 'Sunday' }
                ].find(d => d.value === dayValue);
                
                if (!day) return null;
                
                return (
                <div key={day.value} className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Calendar className="w-5 h-5 text-purple-600 mr-3" />
                        <span className="text-gray-700 dark:text-gray-300 font-medium">{day.label}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const newTimeSlots = { ...timeSlots };
                          if (!newTimeSlots[day.value]) {
                            newTimeSlots[day.value] = [];
                          }
                          // Toggle the add slot interface
                          newTimeSlots[day.value]._showAddSlot = !newTimeSlots[day.value]._showAddSlot;
                          setTimeSlots(newTimeSlots);
                          console.log('Toggled add slot for', day.value, 'Show:', newTimeSlots[day.value]._showAddSlot);
                        }}
                        className="flex items-center text-purple-600 hover:text-purple-700 text-sm"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        {timeSlots[day.value] && timeSlots[day.value]._showAddSlot ? 'Cancel' : 'Add Slot'}
                      </button>
                    </div>
                    
                    {/* Time Slots for this day */}
                    {timeSlots[day.value] && timeSlots[day.value].length > 0 && (
                      <div className="grid grid-cols-2 gap-2">
                        {timeSlots[day.value].filter(slot => !slot.startsWith('_')).map(timeSlot => (
                          <label key={timeSlot} className="flex items-center p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={timeSlots[day.value].includes(timeSlot)}
                              onChange={() => {
                                const newTimeSlots = { ...timeSlots };
                                if (newTimeSlots[day.value].includes(timeSlot)) {
                                  newTimeSlots[day.value] = newTimeSlots[day.value].filter(t => t !== timeSlot);
                                } else {
                                  newTimeSlots[day.value] = [...newTimeSlots[day.value], timeSlot];
                                }
                                setTimeSlots(newTimeSlots);
                              }}
                              className="mr-2 text-purple-600 focus:ring-purple-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {timeSlot}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}

                    {/* Add Time Slot Dropdown */}
                    {timeSlots[day.value] && timeSlots[day.value]._showAddSlot && (
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <input
                            type="time"
                            onChange={(e) => {
                              if (e.target.value) {
                                const [hours, minutes] = e.target.value.split(':');
                                const hour = parseInt(hours);
                                const ampm = hour >= 12 ? 'PM' : 'AM';
                                const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                                const fromTime = `${displayHour.toString().padStart(2, '0')}:${minutes} ${ampm}`;
                                
                                console.log('From time selected:', fromTime, 'for day:', day.value);
                                
                                // Store the from time temporarily
                                const newTimeSlots = { ...timeSlots };
                                newTimeSlots[day.value] = newTimeSlots[day.value] || [];
                                newTimeSlots[day.value]._tempFrom = fromTime;
                                setTimeSlots(newTimeSlots);
                              }
                            }}
                            className="flex-1 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white"
                            placeholder="From time"
                          />
                          <span className="text-sm text-gray-500 dark:text-gray-400">To</span>
                          <input
                            type="time"
                            onChange={(e) => {
                              if (e.target.value && timeSlots[day.value] && timeSlots[day.value]._tempFrom) {
                                const [hours, minutes] = e.target.value.split(':');
                                const hour = parseInt(hours);
                                const ampm = hour >= 12 ? 'PM' : 'AM';
                                const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                                const toTime = `${displayHour.toString().padStart(2, '0')}:${minutes} ${ampm}`;
                                
                                console.log('To time selected:', toTime, 'for day:', day.value);
                                
                                // Generate time slots between from and to times
                                const fromTime = timeSlots[day.value]._tempFrom;
                                const timeRange = generateTimeRange(fromTime, toTime);
                                
                                console.log('Generated time range:', timeRange);
                                
                                const newTimeSlots = { ...timeSlots };
                                // Filter out temporary data and add new time slots
                                const existingSlots = newTimeSlots[day.value].filter(slot => !slot.startsWith('_') && typeof slot === 'string');
                                newTimeSlots[day.value] = [...existingSlots, ...timeRange];
                                delete newTimeSlots[day.value]._tempFrom;
                                setTimeSlots(newTimeSlots);
                                
                                console.log('Updated time slots for', day.value, ':', newTimeSlots[day.value]);
                                
                                // Clear the inputs
                                e.target.value = '';
                                // Clear the from time input
                                const fromInput = e.target.previousElementSibling.previousElementSibling;
                                if (fromInput) fromInput.value = '';
                              }
                            }}
                            className="flex-1 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white"
                            placeholder="To time"
                          />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Select time range (e.g., 12:30 PM to 5:00 PM)
                        </p>
                      </div>
                    )}

                    {/* Remove all slots for this day */}
                    {timeSlots[day.value] && timeSlots[day.value].length > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          const newTimeSlots = { ...timeSlots };
                          delete newTimeSlots[day.value];
                          setTimeSlots(newTimeSlots);
                        }}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Remove all slots for {day.label}
                      </button>
                    )}

                    {/* Copy to all days checkbox - only show for Monday */}
                    {day.value === 'monday' && timeSlots[day.value] && timeSlots[day.value].length > 0 && (
                      <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            onChange={(e) => {
                              if (e.target.checked) {
                                // Copy Monday's time slots to all other days
                                const mondaySlots = timeSlots.monday.filter(slot => !slot.startsWith('_'));
                                const newTimeSlots = { ...timeSlots };
                                
                                ['tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].forEach(dayKey => {
                                  newTimeSlots[dayKey] = [...mondaySlots];
                                });
                                
                                setTimeSlots(newTimeSlots);
                              }
                            }}
                            className="mr-2 text-purple-600 focus:ring-purple-500"
                          />
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            Copy above timings to all days
                          </span>
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              );
              })}
              </div>
              </div>
            </div>
          </div>

          {/* Emergency Consultation Option Section */}
          <div className="space-y-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="space-y-4">
                <div className="flex items-center">
                  <AlertTriangle className="w-5 h-5 text-purple-600 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Emergency Consultation</h3>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Configure emergency consultation availability and fees</p>
              <div className="space-y-4">
                {/* Emergency Consultation Availability */}
                <div className="flex items-center">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.emergencyConsultationAvailable}
                      onChange={(e) => handleInputChange('emergencyConsultationAvailable', e.target.checked)}
                      className="mr-3 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-gray-700 dark:text-gray-300 font-medium">
                      Available for emergency consultation
                    </span>
                  </label>
                </div>

                {/* Emergency Consultation Fee */}
                {formData.emergencyConsultationAvailable && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Emergency consultation fee *
                    </label>
                    <div className="flex items-center bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2">
                      <span className="text-gray-500 dark:text-gray-400 mr-2">₹</span>
                      <input
                        type="number"
                        value={formData.emergencyConsultationFee}
                        onChange={(e) => handleInputChange('emergencyConsultationFee', e.target.value)}
                        placeholder="Enter emergency consultation fee"
                        className="flex-1 bg-transparent border-none outline-none text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Fee for emergency consultations outside regular hours
                    </p>
                  </div>
                )}
              </div>
              </div>
            </div>
          </div>

          {/* File Reading Charges Section */}
          <div className="space-y-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="space-y-4">
                <div className="flex items-center">
                  <FileText className="w-5 h-5 text-purple-600 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">File Reading Charges</h3>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Set charges for reading legal documents based on page ranges</p>
              <div className="space-y-4">
                {/* File Reading Charges List */}
                {formData.fileReadingCharges && formData.fileReadingCharges.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2 mb-2">
                      <FileText className="w-4 h-4 text-purple-600" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Current Charges:</span>
                    </div>
                    {formData.fileReadingCharges.map((charge, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <FileText className="w-4 h-4 text-purple-600" />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {charge.fromPage} - {charge.toPage} pages: ₹{charge.amount}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const newCharges = formData.fileReadingCharges.filter((_, i) => i !== index);
                            setFormData({ ...formData, fileReadingCharges: newCharges });
                          }}
                          className="text-red-500 hover:text-red-700 p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add New File Reading Charge */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-700 dark:text-gray-300 font-medium">Add File Reading Charge</span>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        From Page
                      </label>
                      <input
                        type="number"
                        value={formData.newFileChargeFromPage}
                        onChange={(e) => setFormData({ ...formData, newFileChargeFromPage: e.target.value })}
                        placeholder="1"
                        className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        To Page
                      </label>
                      <input
                        type="number"
                        value={formData.newFileChargeToPage}
                        onChange={(e) => setFormData({ ...formData, newFileChargeToPage: e.target.value })}
                        placeholder="20"
                        className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Amount (₹)
                      </label>
                      <input
                        type="number"
                        value={formData.newFileChargeAmount}
                        onChange={(e) => setFormData({ ...formData, newFileChargeAmount: e.target.value })}
                        placeholder="200"
                        className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                  
                  <button
                    type="button"
                    onClick={() => {
                      const fromPage = formData.newFileChargeFromPage;
                      const toPage = formData.newFileChargeToPage;
                      const amount = formData.newFileChargeAmount;
                      
                      if (fromPage && toPage && amount) {
                        const newCharge = {
                          fromPage: parseInt(fromPage),
                          toPage: parseInt(toPage),
                          amount: parseInt(amount)
                        };
                        
                        const currentCharges = formData.fileReadingCharges || [];
                        const updatedCharges = [...currentCharges, newCharge];
                        
                        setFormData({
                          ...formData,
                          fileReadingCharges: updatedCharges,
                          newFileChargeFromPage: '',
                          newFileChargeToPage: '',
                          newFileChargeAmount: ''
                        });
                      }
                    }}
                    className="flex items-center text-purple-600 hover:text-purple-700 text-sm font-medium"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add File Reading Charge
                  </button>
                </div>
              </div>
              </div>
            </div>
          </div>

          {/* Final Details Section */}
          <div className="space-y-4">
            <div className="px-1">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Final Details</h3>
                <div className="flex items-center bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 px-2 py-1 rounded-full text-xs font-medium">
                  <EyeOff className="w-3 h-3 mr-1" />
                  Admin Only
                </div>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Complete your profile with payment and contact details</p>
              <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                ⚠️ Bank details are only visible to administrators for payment processing
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="space-y-4">
                {/* Bank Details Section */}
                <div className="space-y-3">
                  <div className="flex items-center">
                    <CreditCard className="w-5 h-5 text-purple-600 mr-3" />
                    <span className="text-purple-600 dark:text-purple-400 font-medium">
                      Bank Details (where you will receive your payment)
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Account Number *
                      </label>
                      <input
                        type="text"
                        value={formData.accountNumber}
                        onChange={(e) => handleInputChange('accountNumber', e.target.value)}
                        placeholder="Enter account number"
                        className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Account Holder Name *
                      </label>
                      <input
                        type="text"
                        value={formData.accountHolderName}
                        onChange={(e) => handleInputChange('accountHolderName', e.target.value)}
                        placeholder="Enter account holder name"
                        className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Bank Name *
                      </label>
                      <input
                        type="text"
                        value={formData.bankName}
                        onChange={(e) => handleInputChange('bankName', e.target.value)}
                        placeholder="Enter bank name"
                        className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        IFSC Code *
                      </label>
                      <input
                        type="text"
                        value={formData.ifscCode}
                        onChange={(e) => handleInputChange('ifscCode', e.target.value.toUpperCase())}
                        placeholder="Enter IFSC code"
                        className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                </div>


              </div>
            </div>
          </div>

                    {/* Achievements Section */}
          <div className="space-y-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="space-y-4">
                <div className="flex items-center">
                  <Trophy className="w-5 h-5 text-purple-600 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Achievements</h3>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Add your professional achievements and awards</p>
                {/* Existing Achievements List */}
                {formData.achievements && formData.achievements.length > 0 && (
                  <div className="space-y-2">
                    {formData.achievements.map((achievement, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <Trophy className="w-4 h-4 text-purple-600" />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {achievement.name} ({achievement.year})
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const newAchievements = formData.achievements.filter((_, i) => i !== index);
                            setFormData({ ...formData, achievements: newAchievements });
                          }}
                          className="text-red-500 hover:text-red-700 p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add New Achievement */}
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Name of Achievement
                      </label>
                      <input
                        type="text"
                        value={formData.newAchievementName}
                        onChange={(e) => setFormData({ ...formData, newAchievementName: e.target.value })}
                        placeholder="Enter achievement name"
                        className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Year
                      </label>
                      <input
                        type="number"
                        value={formData.newAchievementYear}
                        onChange={(e) => setFormData({ ...formData, newAchievementYear: e.target.value })}
                        placeholder="2023"
                        min="1900"
                        max="2030"
                        className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                  
                  <button
                    type="button"
                    onClick={() => {
                      const name = formData.newAchievementName;
                      const year = formData.newAchievementYear;
                      
                      if (name && year) {
                        const newAchievement = {
                          name: name,
                          year: parseInt(year)
                        };
                        
                        const currentAchievements = formData.achievements || [];
                        const updatedAchievements = [...currentAchievements, newAchievement];
                        
                        setFormData({
                          ...formData,
                          achievements: updatedAchievements,
                          newAchievementName: '',
                          newAchievementYear: ''
                        });
                      }
                    }}
                    className="flex items-center text-purple-600 hover:text-purple-700 text-sm font-medium"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add More Achievement
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Socials Section */}
          <div className="space-y-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="space-y-4">
                <div className="flex items-center">
                  <Share2 className="w-5 h-5 text-purple-600 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Socials</h3>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Connect your social media profiles</p>
                
                <div className="space-y-4">
                  {/* Gmail */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Gmail *
                    </label>
                    <div className={`flex items-center bg-gray-50 dark:bg-gray-700 border rounded-lg px-3 py-2 ${
                      emailValidationStatus === 'duplicate' 
                        ? 'border-red-500 bg-red-50 dark:bg-red-900/20' 
                        : emailValidationStatus === 'unique' 
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                        : emailValidationStatus === 'checking'
                        ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20'
                        : 'border-gray-200 dark:border-gray-600'
                    }`}>
                      <Mail className="w-4 h-4 text-purple-600 mr-2" />
                      <input
                        type="email"
                        value={formData.gmail}
                        onChange={(e) => handleInputChange('gmail', e.target.value)}
                        placeholder="Enter your Gmail address"
                        className="flex-1 bg-transparent border-none outline-none text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      />
                      {emailValidationStatus === 'checking' && (
                        <div className="w-4 h-4 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin ml-2" />
                      )}
                      {emailValidationStatus === 'unique' && (
                        <CheckCircle className="w-4 h-4 text-green-500 ml-2" />
                      )}
                      {emailValidationStatus === 'duplicate' && (
                        <AlertCircle className="w-4 h-4 text-red-500 ml-2" />
                      )}
                    </div>
                    {emailValidationStatus === 'duplicate' && (
                      <p className="text-xs text-red-500 mt-1">
                        ❌ A lawyer profile already exists with this email address
                      </p>
                    )}
                    {emailValidationStatus === 'unique' && (
                      <p className="text-xs text-green-500 mt-1">
                        ✅ Email is available for registration
                      </p>
                    )}
                  </div>

                  {/* LinkedIn */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      LinkedIn
                    </label>
                    <div className="flex items-center bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2">
                      <Linkedin className="w-4 h-4 text-purple-600 mr-2" />
                      <input
                        type="url"
                        value={formData.linkedin}
                        onChange={(e) => handleInputChange('linkedin', e.target.value)}
                        placeholder="Enter your LinkedIn profile URL"
                        className="flex-1 bg-transparent border-none outline-none text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      />
                    </div>
                  </div>

                  {/* Facebook */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Facebook
                    </label>
                    <div className="flex items-center bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2">
                      <Facebook className="w-4 h-4 text-purple-600 mr-2" />
                      <input
                        type="url"
                        value={formData.facebook}
                        onChange={(e) => handleInputChange('facebook', e.target.value)}
                        placeholder="Enter your Facebook profile URL"
                        className="flex-1 bg-transparent border-none outline-none text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      />
                    </div>
                  </div>

                  {/* Twitter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Twitter
                    </label>
                    <div className="flex items-center bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2">
                      <Twitter className="w-4 h-4 text-purple-600 mr-2" />
                      <input
                        type="url"
                        value={formData.twitter}
                        onChange={(e) => handleInputChange('twitter', e.target.value)}
                        placeholder="Enter your Twitter profile URL"
                        className="flex-1 bg-transparent border-none outline-none text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      />
                    </div>
                  </div>

                  {/* Instagram */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Instagram
                    </label>
                    <div className="flex items-center bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2">
                      <Instagram className="w-4 h-4 text-purple-600 mr-2" />
                      <input
                        type="url"
                        value={formData.instagram}
                        onChange={(e) => handleInputChange('instagram', e.target.value)}
                        placeholder="Enter your Instagram profile URL"
                        className="flex-1 bg-transparent border-none outline-none text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Terms & Conditions */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="space-y-4">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Terms & Conditions</h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Please read and agree to our terms and conditions before creating your profile.
                </p>
                
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="termsAgreement"
                    checked={formData.termsAgreement}
                    onChange={(e) => handleInputChange('termsAgreement', e.target.checked)}
                    className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <label htmlFor="termsAgreement" className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                    I agree to the platform <span className="text-purple-600 dark:text-purple-400 font-medium">Terms and Conditions</span> and <span className="text-purple-600 dark:text-purple-400 font-medium">Privacy Policy</span>. I confirm that all information provided is accurate and I understand that my profile will be reviewed before approval.
                  </label>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Hidden canvas for photo capture */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Fixed Bottom Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4 max-w-md mx-auto">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-4 px-6 rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl"
        >
          {isSubmitting ? 'Creating...' : t.createProfile}
        </button>
      </div>
    </div>
  );
};

export default LawyerRegistration;